import { useState, useEffect, useRef, useCallback } from "react";
import HelpSystem from './HelpSystem.jsx';
import FeedbackButton from './FeedbackButton.jsx';
import { APP_NAME, APP_NAME_UPPER, CONTACT_EMAIL } from './brand';
import { createClient } from "@supabase/supabase-js";
import {
  localDateKey,
  dateFromLocalKey,
  shouldShowLateNightPrompt,
  computeStaleEventRowIds,
  resolveSavedEvents,
  sortJournalEntries,
} from "./utils/appLogic";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const C = {
  bg: "#060d14", surface: "#0b1820", card: "#0f2129", border: "#1c3d47",
  accent: "#1fd8f5", accentDim: "#0a3a45",
  text: "#eef4ff", textMid: "#a3b8cc", textDim: "#6e93a3",
};

// Page backdrop: near-black navy fading to deep teal.
const BG_GRADIENT = "linear-gradient(180deg, #05050f 0%, #03282d 60%, #04464f 100%)";
const HEAD_FONT = "'Bebas Neue',Impact,sans-serif";

const TAG_PALETTE = [
  { color: "#e8365d", bg: "#3d1220" }, { color: "#6c63ff", bg: "#1e1b3d" },
  { color: "#10b981", bg: "#0d2e22" }, { color: "#f59e0b", bg: "#2d2010" },
  { color: "#38bdf8", bg: "#0c2233" }, { color: "#f472b6", bg: "#2d1020" },
  { color: "#a78bfa", bg: "#1e1533" }, { color: "#fb923c", bg: "#2d1808" },
  { color: "#34d399", bg: "#0a2318" }, { color: "#e879f9", bg: "#2a0e2e" },
];

const SEED_TAGS = [
  { id: "work",        label: "work",        ...TAG_PALETTE[0], pinned: true },
  { id: "personal",    label: "personal",    ...TAG_PALETTE[1], pinned: true },
  { id: "surviving",   label: "surviving",   ...TAG_PALETTE[2], pinned: true },
  { id: "human stuff", label: "human stuff", ...TAG_PALETTE[3], pinned: true },
];


const PROMOTE_THRESHOLD = 3;
const SNAP_MINUTES = 5;
const DEFAULT_DURATION = 30;
const PIXELS_PER_MINUTE = 1.2;
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 23;
const TOTAL_MINUTES = (DAY_END_HOUR - DAY_START_HOUR) * 60;

const MOODS = ["🌑 crashed", "🌘 low", "🌗 okay", "🌕 good", "⭐ lit"];

const JOURNAL_TYPES = ["note", "done", "stuck", "follow_up"];
const JOURNAL_TYPE_LABELS = {
  note: "note",
  done: "done",
  stuck: "stuck",
  follow_up: "follow up",
};
const JOURNAL_PROMPTS_DAY = [
  "what happened that's worth remembering?",
  "what got stuck?",
  "what needs follow-up?",
  "what helped?",
  "what got finished?",
  // for when you're not sure what to say: small, easy, no wrong answers
  "what's one word for how today feels so far?",
  "what's on your mind right now, even if it's small?",
  "what's the smallest thing you got done?",
  "what did you notice today?",
  "what made you smile, even a little?",
  "what's taking up space in your head?",
  "what do you need right now?",
  "what went better than expected?",
  "how's your energy, honestly?",
  "what's something you keep circling around?",
  "what did you eat or drink that was good?",
  "who did you talk to today?",
  "what's something you're looking forward to?",
  "what would make the rest of today a little easier?",
  "what's one thing you can let go of today?",
  "what took more effort than it should have?",
  "what didn't go to plan, and what happened instead?",
  "what are you proud of, even if it feels tiny?",
  "what is your body telling you right now?",
  "what's something small you did for yourself?",
  "what do you want to remember about today?",
  "what did you learn, even by accident?",
  "what song, show, or snack is getting you through?",
  "what surprised you?",
  "what are you grateful for right now, even if it's just the coffee?",
  "what's a thought you keep having?",
  "what are you curious about right now?",
  "what would you tell a friend who had your morning?",
  "what's the weather like in your head today?",
  "describe the last five minutes in one sentence.",
];
const JOURNAL_PROMPTS_EOD = [
  "what should tomorrow start with?",
  "what is still open?",
  "anything worth carrying forward?",
  "what mattered today?",
  // for when you're not sure what to say: small, easy, no wrong answers
  "if today had a title, what would it be?",
  "what was the best five minutes of today?",
  "what was the hardest part, and how did you get through it?",
  "what's one thing you're glad you did today?",
  "what are you letting go of before bed?",
  "what's one thing you're looking forward to tomorrow?",
  "who or what helped today?",
  "what made you laugh or smile?",
  "did your body get what it needed today?",
  "what drained you, and what gave you energy?",
  "what do you want to remember about today?",
  "what's one small win, even if it doesn't look like one?",
  "what did you do today that nobody saw?",
  "what's one thing that would make tomorrow easier?",
  "what are you proud of today, even a little?",
  "what felt heavier than it should have?",
  "what would you tell a friend who had the day you had?",
  "what's still on your mind as the day ends?",
  "what surprised you today?",
  "what are you grateful for tonight?",
  "how do you feel right now, in one word?",
  "what did today teach you about what you need?",
  "what can wait until tomorrow?",
  "what got done that wasn't even on the list?",
  "what part of today do you want to keep?",
  "was today a high-energy, low-energy, or in-between day? what made it so?",
  "describe today as the weather, inside and out.",
];

function genId() { return Math.random().toString(36).slice(2, 9); }
function today() { return new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }); }
function todayKey() { return localDateKey(); }
function nowStamp() {
  const d = new Date();
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function hashString(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}
function pickPrompt(pool, key, entryCount, offset) {
  if (!pool.length) return "";
  const base = hashString(`${key}:${entryCount}`);
  return pool[(base + offset) % pool.length];
}
function formatEntryTime(isoStamp) {
  if (!isoStamp) return "";
  const dt = new Date(isoStamp);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function formatEventDate(dateStr) {
  const d = dateFromLocalKey(dateStr);
  const t = new Date(); t.setHours(0,0,0,0);
  const diff = Math.round((d - t) / 86400000);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff === -1) return "yesterday";
  if (diff > 0 && diff < 7) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function isToday(dateStr) { return dateStr === todayKey(); }
function isFuture(dateStr) { return dateStr >= todayKey(); }
function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
function escapeHtmlWithBreaks(value) {
  return escapeHtml(value).replace(/\r?\n/g, "<br>");
}

// ── Time math ─────────────────────────────────────────────────────────
function timeToMinutes(timeStr) {
  if (!timeStr) return DAY_START_HOUR * 60;
  const match = timeStr.match(/(\d+):(\d+)(am|pm)/i);
  if (!match) return DAY_START_HOUR * 60;
  let h = parseInt(match[1]);
  const m = parseInt(match[2]);
  const ampm = match[3].toLowerCase();
  if (ampm === 'pm' && h !== 12) h += 12;
  if (ampm === 'am' && h === 12) h = 0;
  return h * 60 + m;
}

function minutesToTimeStr(totalMinutes) {
  const h = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  const ampm = h < 12 ? "am" : "pm";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")}${ampm}`;
}

function snapToGrid(minutes) {
  return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
}

function minutesToPx(minutes) {
  return minutes * PIXELS_PER_MINUTE;
}

function pxToMinutes(px) {
  return px / PIXELS_PER_MINUTE;
}


// ── Column layout for overlapping blocks ──────────────────────────────
function computeColumns(blocks) {
  const sorted = [...blocks].sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  const columns = [];
  const blockCols = {};

  for (const block of sorted) {
    const start = timeToMinutes(block.time);
    const end = start + (block.duration || DEFAULT_DURATION);
    let placed = false;
    for (let col = 0; col < columns.length; col++) {
      const lastEnd = columns[col];
      if (start >= lastEnd) {
        columns[col] = end;
        blockCols[block.id] = col;
        placed = true;
        break;
      }
    }
    if (!placed) {
      blockCols[block.id] = columns.length;
      columns.push(end);
    }
  }

  const blockWidths = {};
  for (const block of sorted) {
    const start = timeToMinutes(block.time);
    const end = start + (block.duration || DEFAULT_DURATION);
    const col = blockCols[block.id];
    let maxCol = col;
    for (const other of sorted) {
      if (other.id === block.id) continue;
      const oStart = timeToMinutes(other.time);
      const oEnd = oStart + (other.duration || DEFAULT_DURATION);
      if (oStart < end && oEnd > start) {
        maxCol = Math.max(maxCol, blockCols[other.id]);
      }
    }
    blockWidths[block.id] = { col, totalCols: maxCol + 1 };
  }

  return blockWidths;
}

// ── Tag helpers ───────────────────────────────────────────────────────
function resolveTag(tagId, tags) {
  return tags.find(t => t.id === tagId) || { label: tagId, color: C.textDim, bg: C.surface };
}
function getOrCreateTag(label, tags) {
  const norm = label.trim().toLowerCase();
  const existing = tags.find(t => t.id === norm);
  if (existing) return { tag: existing, tags };
  const usedPalette = tags.filter(t => t.color && !t.uncolored).length;
  const p = TAG_PALETTE[usedPalette % TAG_PALETTE.length];
  const newTag = { id: norm, label: norm, color: p.color, bg: p.bg, uses: 1, uncolored: false };
  return { tag: newTag, tags: [...tags, newTag] };
}
function bumpTagUse(tagId, tags) {
  return tags.map(t => t.id !== tagId ? t : { ...t, uses: (t.uses || 0) + 1 });
}

// ── CHANGE 3: makeDefaults removed — blocks always start empty ────────
// Demo blocks are gone. New days start with [].

// ── Pattern helpers ───────────────────────────────────────────────────
const STOP = new Set(["the","a","an","and","or","but","i","my","to","was","it","in","of","that","so","just","is","on","at","for","with","had","not","no","be","have","did","got","this","what","when","went","felt","really","very","like","time","day","today","some","more","too","been","then","also","into"]);
function topWords(text, n = 6) {
  const freq = {};
  text.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/).filter(w => w.length > 3 && !STOP.has(w)).forEach(w => { freq[w] = (freq[w] || 0) + 1; });
  return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, n);
}
function computePatterns(archive, tags) {
  const days = Object.values(archive);
  if (!days.length) return null;
  const moodData = days.slice(-14).map(d => ({ date: d.date, mood: d.mood ?? 2 }));
  const avgMood = +(moodData.reduce((s, d) => s + d.mood, 0) / moodData.length).toFixed(1);
  const byDow = {};
  days.forEach(d => {
    if (!d.key) return;
    const dow = dateFromLocalKey(d.key).toLocaleDateString("en-US", { weekday: "short" });
    if (!byDow[dow]) byDow[dow] = [];
    byDow[dow].push(d.mood ?? 2);
  });
  const dowAvg = Object.entries(byDow).map(([dow, ms]) => ({ dow, avg: ms.reduce((s, m) => s + m, 0) / ms.length })).sort((a, b) => b.avg - a.avg);
  const tagCount = {};
  days.forEach(d => (d.blocks || []).forEach(b => { if (b.tag) tagCount[b.tag] = (tagCount[b.tag] || 0) + 1; }));
  const tagDist = Object.entries(tagCount).sort((a, b) => b[1] - a[1]);
  const totalBlocks = tagDist.reduce((s, [, n]) => s + n, 0) || 1;
  let streak = 0, checkDate = new Date();
  for (let i = 0; i < 60; i++) {
    const k = localDateKey(checkDate);
    if (archive[k]) { streak++; checkDate.setDate(checkDate.getDate() - 1); }
    else if (i === 0) { checkDate.setDate(checkDate.getDate() - 1); }
    else break;
  }
  const taskDays = days.filter(d => d.tasks && d.tasks.length > 0);
  const taskCompletionRate = taskDays.length > 0
    ? Math.round(taskDays.reduce((s, d) => { const done = d.tasks.filter(t => t.done).length; return s + (done / d.tasks.length); }, 0) / taskDays.length * 100)
    : null;
  return {
    moodData, avgMood, dowAvg, tagDist, totalBlocks, streak,
    totalDays: days.length, taskCompletionRate,
    winWords: topWords(days.map(d => d.wins || "").join(" ")),
    derailWords: topWords(days.map(d => d.hard || "").join(" ")),
  };
}

// ── Print ─────────────────────────────────────────────────────────────
function printDay(day, tags) {
  const resolveT = (tagId) => tags.find(t => t.id === tagId) || { label: tagId, color: "#6e93a3", bg: "#0d1d21" };
  const MOODS_PRINT = ["🌑 crashed", "🌘 low", "🌗 okay", "🌕 good", "⭐ lit"];
  const doneTasks = (day.tasks || []).filter(t => t.done);
  const pendingTasks = (day.tasks || []).filter(t => !t.done);
  const ROLLOVER_THRESHOLD = 3;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${APP_NAME} - ${day.date || day.key}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=Bebas+Neue&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }
html, body { width: 5.5in; height: 8.5in; background: #fff; }
body { display: flex; flex-direction: row; font-family: 'DM Sans', sans-serif; color: #1a1a1a; overflow: hidden; }
.content { flex: 1; padding: 28px 22px 24px 26px; display: flex; flex-direction: column; }
.doodle { width: 108px; flex-shrink: 0; background: #fafafa; border-left: 1px solid #e0e0e0; position: relative; overflow: hidden; }
.doodle::before { content: ''; position: absolute; inset: 0; background-image: radial-gradient(circle, #c8c8c8 0.8px, transparent 0.8px); background-size: 10px 10px; background-position: 5px 5px; }
.page-header { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 10px; border-bottom: 2.5px solid #e8365d; margin-bottom: 14px; }
.flux-word { font-family: 'Bebas Neue', sans-serif; font-size: 20px; letter-spacing: 3px; color: #e8365d; line-height: 1; }
.sub { font-size: 7px; letter-spacing: 2px; color: #bbb; text-transform: uppercase; font-family: 'DM Mono', monospace; margin-top: 1px; }
.dow { font-family: 'Bebas Neue', sans-serif; font-size: 15px; letter-spacing: 2px; color: #1a1a1a; line-height: 1; text-align: right; }
.date-str { font-size: 9px; color: #999; margin-top: 1px; font-family: 'DM Mono', monospace; text-align: right; }
.energy-row { display: flex; align-items: center; gap: 8px; margin-bottom: 13px; }
.energy-lbl { font-size: 7px; letter-spacing: 2px; color: #aaa; text-transform: uppercase; font-family: 'DM Mono', monospace; min-width: 44px; }
.dots { display: flex; gap: 4px; }
.dot { width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid #ddd; }
.dot.on { background: #e8365d; border-color: #e8365d; }
.energy-val { font-size: 9px; color: #999; font-style: italic; }
.sec-label { font-size: 7px; letter-spacing: 2.5px; text-transform: uppercase; color: #e8365d; font-family: 'DM Mono', monospace; font-weight: 500; margin-bottom: 5px; padding-bottom: 3px; border-bottom: 1px solid #f2f2f2; }
.schedule { margin-bottom: 13px; }
.block-row { display: flex; align-items: flex-start; gap: 7px; padding: 3px 0; border-bottom: 1px solid #f8f8f8; }
.b-time { font-size: 8.5px; color: #e8365d; min-width: 42px; font-family: 'DM Mono', monospace; padding-top: 2px; flex-shrink: 0; }
.b-dur { font-size: 8px; color: #bbb; font-family: 'DM Mono', monospace; min-width: 32px; }
.b-dot { width: 6px; height: 6px; border-radius: 50%; margin-top: 4px; flex-shrink: 0; }
.b-label { font-size: 11px; color: #1a1a1a; line-height: 1.3; }
.b-tag { font-size: 8px; color: #bbb; font-family: 'DM Mono', monospace; }
.b-note { font-size: 8px; color: #aaa; font-style: italic; }
.tasks { margin-bottom: 13px; }
.task-row { display: flex; align-items: center; gap: 7px; padding: 3.5px 0; border-bottom: 1px solid #f8f8f8; }
.cb { width: 11px; height: 11px; border-radius: 2px; border: 1.5px solid #ccc; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 7px; }
.cb.done { background: #10b981; border-color: #10b981; color: #fff; }
.t-label { flex: 1; font-size: 11px; color: #1a1a1a; line-height: 1.3; }
.t-label.crossed { text-decoration: line-through; color: #bbb; }
.t-done-time { font-size: 8px; color: #ccc; font-family: 'DM Mono', monospace; flex-shrink: 0; }
.rolled { color: #e8365d; font-size: 9px; margin-left: 2px; }
.journal { margin-bottom: 10px; }
.j-content { font-size: 10.5px; color: #333; line-height: 1.6; margin-bottom: 4px; font-style: italic; }
.wline { border-bottom: 1px solid #ebebeb; height: 18px; }
.page-footer { margin-top: auto; padding-top: 10px; border-top: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
.f-flux { font-family: 'Bebas Neue', sans-serif; letter-spacing: 2px; color: #ddd; font-size: 11px; }
.f-id { font-size: 8px; color: #ccc; font-family: 'DM Mono', monospace; }
.f-note { font-size: 8px; color: #ccc; font-family: 'DM Mono', monospace; }
.corner-geo { position: absolute; bottom: 0; right: 0; width: 90px; height: 90px; }
@media print { html, body { width: 5.5in; height: 8.5in; } @page { size: 5.5in 8.5in; margin: 0; } }
</style>
</head>
<body>
<div class="content">
  <div class="page-header">
    <div><div class="flux-word">${APP_NAME_UPPER}</div><div class="sub">daily log</div></div>
    <div>
      <div class="dow">${dateFromLocalKey(day.key || todayKey()).toLocaleDateString("en-US", { weekday: "long" })}</div>
      <div class="date-str">${escapeHtml(day.date || day.key)}</div>
    </div>
  </div>
  <div class="energy-row">
    <div class="energy-lbl">Energy</div>
    <div class="dots">${[0,1,2,3,4].map(i => `<div class="dot${i <= (day.mood ?? 2) ? " on" : ""}"></div>`).join("")}</div>
    <div class="energy-val">${MOODS_PRINT[day.mood ?? 2]}</div>
  </div>
  <div class="schedule">
    <div class="sec-label">Schedule</div>
    ${(day.blocks || []).sort((a,b) => timeToMinutes(a.time) - timeToMinutes(b.time)).map(b => {
      const t = resolveT(b.tag);
      const dur = b.duration || DEFAULT_DURATION;
      const durStr = dur >= 60 ? `${Math.floor(dur/60)}h${dur%60 ? dur%60+"m" : ""}` : `${dur}m`;
      return `<div class="block-row">
        <div class="b-time">${escapeHtml(b.time)}</div>
        <div class="b-dur">${escapeHtml(durStr)}</div>
        <div class="b-dot" style="background:${t.color}"></div>
        <div>
          <div class="b-label">${escapeHtml(b.label)}</div>
          <div class="b-tag">${escapeHtml(t.label)}</div>
          ${b.note ? `<div class="b-note">${escapeHtmlWithBreaks(b.note)}</div>` : ""}
        </div>
      </div>`;
    }).join("")}
  </div>
  ${(day.tasks || []).length > 0 ? `
  <div class="tasks">
    <div class="sec-label">Tasks</div>
    ${doneTasks.map(t => `<div class="task-row"><div class="cb done">✓</div><div class="t-label crossed">${escapeHtml(t.label)}</div><div class="t-done-time">${escapeHtml(t.doneAt || "")}</div></div>`).join("")}
    ${pendingTasks.map(t => {
      const rolledCount = (t.addedAt || "").split("(rolled)").length - 1;
      return `<div class="task-row"><div class="cb"></div><div class="t-label">${escapeHtml(t.label)}${rolledCount >= ROLLOVER_THRESHOLD ? '<span class="rolled">*</span>' : ""}</div></div>`;
    }).join("")}
  </div>` : ""}
  ${day.wins ? `<div class="journal"><div class="sec-label">Wins</div><div class="j-content">${escapeHtmlWithBreaks(day.wins)}</div><div class="wline"></div><div class="wline"></div></div>` : `<div class="journal"><div class="sec-label">Wins</div><div class="wline"></div><div class="wline"></div><div class="wline"></div></div>`}
  ${day.hard ? `<div class="journal"><div class="sec-label">Hard stuff</div><div class="j-content">${escapeHtmlWithBreaks(day.hard)}</div><div class="wline"></div><div class="wline"></div></div>` : `<div class="journal"><div class="sec-label">Hard stuff</div><div class="wline"></div><div class="wline"></div><div class="wline"></div></div>`}
  <div class="page-footer">
    <div class="f-flux">${APP_NAME_UPPER}</div>
    <div class="f-note">* rolled 3+ days</div>
    <div class="f-id">${escapeHtml(day.key || "")}</div>
  </div>
</div>
<div class="doodle">
  <svg class="corner-geo" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="58" cy="62" r="18" stroke="#e8365d" stroke-width="1.2" opacity="0.35"/>
    <polygon points="30,82 52,82 41,62" stroke="#e8365d" stroke-width="1.2" fill="none" opacity="0.3"/>
    <rect x="60" y="40" width="16" height="16" stroke="#e8365d" stroke-width="1.2" fill="none" opacity="0.25"/>
    <circle cx="32" cy="58" r="4" stroke="#e8365d" stroke-width="1.2" fill="none" opacity="0.3"/>
    <line x1="20" y1="88" x2="88" y2="88" stroke="#e8365d" stroke-width="1" opacity="0.2"/>
    <line x1="88" y1="20" x2="88" y2="88" stroke="#e8365d" stroke-width="1" opacity="0.2"/>
  </svg>
</div>
<script>window.onload = () => window.print();</script>
</body>
</html>`;
  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
}

// ── Mini components ───────────────────────────────────────────────────
function MiniBar({ label, value, max, color, sub }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: C.text }}>{label}</span>
        <span style={{ fontSize: 11, color: C.textDim }}>{sub}</span>
      </div>
      <div style={{ height: 3, background: C.border, borderRadius: 2 }}>
        <div style={{ height: 3, width: `${pct}%`, background: color, borderRadius: 2, transition: "width .6s ease" }} />
      </div>
    </div>
  );
}
function Sparkline({ data }) {
  if (!data || data.length < 2) return null;
  const W = 280, H = 44, P = 4;
  const pts = data.map((d, i) => {
    const x = P + (i / (data.length - 1)) * (W - P * 2);
    const y = H - P - ((d.mood / 4) * (H - P * 2));
    return `${x},${y}`;
  }).join(" ");
  const last = data[data.length - 1];
  const lx = W - P, ly = H - P - ((last.mood / 4) * (H - P * 2));
  return (
    <svg width={W} height={H} style={{ overflow: "visible" }}>
      <polyline points={pts} fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r="3" fill={C.accent} />
    </svg>
  );
}
function TagPill({ tag, small, onClick }) {
  return (
    <span onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: small ? "2px 7px" : "3px 9px", borderRadius: 3, fontSize: small ? 10 : 11, background: tag.bg || C.surface, border: `1px solid ${tag.color || C.border}40`, color: tag.color || C.textDim, cursor: onClick ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap" }}>{tag.label}</span>
  );
}
function TagSelector({ tags, value, onChange, onCreateTag }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const ref = useRef();
  const current = tags.find(t => t.id === value);
  const filtered = input.trim() ? tags.filter(t => t.label.includes(input.toLowerCase())) : tags;
  const canCreate = input.trim() && !tags.find(t => t.id === input.trim().toLowerCase());
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  function select(tag) { onChange(tag.id); setOpen(false); setInput(""); }
  function create() {
    if (!input.trim()) return;
    const { tag, tags: newTags } = getOrCreateTag(input, tags);
    onCreateTag(newTags); onChange(tag.id); setOpen(false); setInput("");
  }
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <div onClick={() => setOpen(!open)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
        {current ? <TagPill tag={current} small /> : <span style={{ fontSize: 11, color: C.textDim, padding: "2px 8px", border: `1px dashed ${C.border}`, borderRadius: 3 }}>tag</span>}
        <span style={{ fontSize: 9, color: C.textDim }}>▾</span>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, minWidth: 170, boxShadow: "0 8px 24px #00000060", padding: "8px 0" }}>
          <input autoFocus placeholder="search or create..." value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") canCreate ? create() : filtered[0] && select(filtered[0]); if (e.key === "Escape") setOpen(false); }}
            style={{ width: "100%", background: C.surface, border: "none", borderBottom: `1px solid ${C.border}`, color: C.text, fontSize: 12, padding: "7px 12px", outline: "none" }} />
          <div style={{ maxHeight: 160, overflowY: "auto", padding: "4px 0" }}>
            {filtered.map(t => (
              <div key={t.id} onClick={() => select(t)} style={{ padding: "7px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, background: value === t.id ? C.surface : "none" }}
                onMouseEnter={e => e.currentTarget.style.background = C.surface}
                onMouseLeave={e => e.currentTarget.style.background = value === t.id ? C.surface : "none"}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: C.text }}>{t.label}</span>
                {t.uses >= PROMOTE_THRESHOLD && <span style={{ fontSize: 9, color: C.textDim, marginLeft: "auto" }}>★</span>}
              </div>
            ))}
            {canCreate && (
              <div onClick={create} style={{ padding: "7px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, borderTop: `1px solid ${C.border}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.surface}
                onMouseLeave={e => e.currentTarget.style.background = "none"}>
                <span style={{ fontSize: 11, color: C.accent }}>+ create</span>
                <span style={{ fontSize: 12, color: C.textMid }}>"{input.trim()}"</span>
              </div>
            )}
            {filtered.length === 0 && !canCreate && <div style={{ padding: "10px 12px", fontSize: 12, color: C.textDim }}>no matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Visual Timeline ───────────────────────────────────────────────────
function VisualTimeline({ blocks, onBlocksChange, tags, onPersistTags, scrollContainerRef, gcalEvents = [] }) {
  const timelineRef = useRef(null);
  const nowRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [resizingId, setResizingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [addingAt, setAddingAt] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [newTag, setNewTag] = useState("work");
  const dragStartY = useRef(0);
  const dragStartMinutes = useRef(0);
  const resizeStartY = useRef(0);
  const resizeStartDuration = useRef(0);

  const timelineHeight = minutesToPx(TOTAL_MINUTES);
  const colLayout = computeColumns(blocks);

  const hourMarkers = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) {
    const mins = (h - DAY_START_HOUR) * 60;
    hourMarkers.push({ mins, label: minutesToTimeStr(h * 60) });
  }

  function getRelativeY(e) {
    const rect = timelineRef.current.getBoundingClientRect();
    return (e.clientY || e.touches?.[0]?.clientY || 0) - rect.top;
  }

  function onBlockMouseDown(e, blockId) {
    if (e.target.dataset.resize || e.target.dataset.edit) return;
    e.preventDefault();
    const block = blocks.find(b => b.id === blockId);
    dragStartY.current = e.clientY;
    dragStartMinutes.current = timeToMinutes(block.time);
    setDraggingId(blockId);

    function onMove(ev) {
      const dy = ev.clientY - dragStartY.current;
      const deltaMinutes = pxToMinutes(dy);
      let newStart = snapToGrid(dragStartMinutes.current + deltaMinutes);
      newStart = Math.max(DAY_START_HOUR * 60, Math.min((DAY_END_HOUR * 60) - (block.duration || DEFAULT_DURATION), newStart));
      onBlocksChange(blocks.map(b => b.id === blockId ? { ...b, time: minutesToTimeStr(newStart) } : b));
    }
    function onUp() {
      setDraggingId(null);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    }
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  function onResizeMouseDown(e, blockId) {
    e.preventDefault();
    e.stopPropagation();
    const block = blocks.find(b => b.id === blockId);
    resizeStartY.current = e.clientY;
    resizeStartDuration.current = block.duration || DEFAULT_DURATION;
    setResizingId(blockId);

    function onMove(ev) {
      const dy = ev.clientY - resizeStartY.current;
      const deltaMins = pxToMinutes(dy);
      const newDur = Math.max(SNAP_MINUTES, snapToGrid(resizeStartDuration.current + deltaMins));
      onBlocksChange(blocks.map(b => b.id === blockId ? { ...b, duration: newDur } : b));
    }
    function onUp() {
      setResizingId(null);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    }
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  function onTimelineClick(e) {
    if (draggingId || resizingId) return;
    if (e.target !== timelineRef.current && !e.target.classList.contains("tl-bg")) return;
    const y = getRelativeY(e);
    const mins = snapToGrid(DAY_START_HOUR * 60 + pxToMinutes(y));
    setAddingAt(mins);
    setNewLabel("");
    setNewTag("work");
  }

  function addBlock() {
    if (!newLabel.trim()) return;
    const newBlock = {
      id: genId(), tag: newTag, label: newLabel.trim(),
      time: minutesToTimeStr(addingAt), duration: DEFAULT_DURATION, note: ""
    };
    onBlocksChange([...blocks, newBlock]);
    setAddingAt(null); setNewLabel("");
  }

  function startEdit(block) {
    setEditingId(block.id);
    setEditValues({ label: block.label, tag: block.tag, duration: block.duration || DEFAULT_DURATION, note: block.note || "" });
  }
  function saveEdit(blockId) {
    onBlocksChange(blocks.map(b => b.id === blockId ? { ...b, ...editValues } : b));
    setEditingId(null);
  }

  function formatDuration(mins) {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const nowTop = minutesToPx(nowMins - DAY_START_HOUR * 60);
  const nowVisible = nowMins >= DAY_START_HOUR * 60 && nowMins <= DAY_END_HOUR * 60;

  // Scroll container to show current time on mount
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container || !nowVisible) return;
    const offset = Math.max(0, nowTop - container.clientHeight * 0.35);
    container.scrollTop = offset;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div style={{ display: "flex", fontSize: 10, color: C.textDim, marginBottom: 8, gap: 16 }}>
        <span>click empty space to add · drag to move · drag bottom to resize</span>
      </div>

      <div style={{ display: "flex", gap: 0 }}>
        {/* Hour labels column */}
        <div style={{ width: 52, flexShrink: 0, position: "relative", height: timelineHeight }}>
          {hourMarkers.map(({ mins, label }) => (
            <div key={mins} style={{ position: "absolute", top: minutesToPx(mins), right: 8, fontSize: 9, color: C.textDim, lineHeight: 1, transform: "translateY(-50%)", whiteSpace: "nowrap" }}>
              {label}
            </div>
          ))}
        </div>

        {/* Timeline body */}
        <div style={{ flex: 1, position: "relative", borderLeft: `1px solid ${C.border}` }}>
          {/* Background grid */}
          <div
            ref={timelineRef}
            className="tl-bg"
            onClick={onTimelineClick}
            style={{ position: "absolute", inset: 0, height: timelineHeight, cursor: "crosshair" }}
          >
            {hourMarkers.map(({ mins }) => (
              <div key={mins} style={{ position: "absolute", top: minutesToPx(mins), left: 0, right: 0, borderTop: `1px solid ${C.border}30`, pointerEvents: "none" }} />
            ))}
            {hourMarkers.slice(0, -1).map(({ mins }) => (
              <div key={`h${mins}`} style={{ position: "absolute", top: minutesToPx(mins + 30), left: 0, right: 0, borderTop: `1px dashed ${C.border}20`, pointerEvents: "none" }} />
            ))}
          </div>

          {/* Now indicator */}
          {nowVisible && (
            <div ref={nowRef} style={{ position: "absolute", top: nowTop, left: 0, right: 0, zIndex: 10, pointerEvents: "none" }}>
              <div style={{ position: "absolute", left: -5, top: -4, width: 8, height: 8, borderRadius: "50%", background: C.accent }} />
              <div style={{ borderTop: `1px solid ${C.accent}`, opacity: 0.7 }} />
            </div>
          )}

          {/* Blocks */}
          <div style={{ position: "relative", height: timelineHeight, pointerEvents: "none" }}>
            {blocks.map(block => {
              const tag = resolveTag(block.tag, tags);
              const startMins = timeToMinutes(block.time);
              const duration = block.duration || DEFAULT_DURATION;
              const top = minutesToPx(startMins - DAY_START_HOUR * 60);
              const height = Math.max(minutesToPx(duration), 24);
              const layout = colLayout[block.id] || { col: 0, totalCols: 1 };
              const colWidth = 100 / layout.totalCols;
              const left = `${layout.col * colWidth}%`;
              const width = `${colWidth - 1}%`;
              const isDragging = draggingId === block.id;
              const isResizing = resizingId === block.id;
              const isEditing = editingId === block.id;

              return (
                <div key={block.id} style={{ pointerEvents: "auto" }}>
                  <div
                    onMouseDown={e => onBlockMouseDown(e, block.id)}
                    style={{
                      position: "absolute", top, left, width, height,
                      background: isDragging || isResizing ? tag.bg : C.card,
                      border: `1px solid ${isDragging || isResizing ? tag.color : C.border}`,
                      borderLeft: `3px solid ${tag.color}`,
                      borderRadius: 4,
                      cursor: isDragging ? "grabbing" : "grab",
                      opacity: isDragging ? 0.85 : 1,
                      zIndex: isDragging || isResizing ? 20 : 1,
                      boxShadow: isDragging ? `0 4px 16px ${tag.color}40` : "none",
                      transition: isDragging || isResizing ? "none" : "box-shadow .15s",
                      overflow: "visible",
                      userSelect: "none",
                    }}
                  >
                    <div style={{ padding: "4px 6px 4px 7px", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 4, flex: 1, overflow: "hidden" }}>
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <div style={{ fontSize: 11, color: C.text, fontWeight: 500, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: height < 40 ? "nowrap" : "normal" }}>{block.label}</div>
                          {height > 36 && <div style={{ fontSize: 9, color: tag.color, opacity: .8, marginTop: 1 }}>{tag.label} · {formatDuration(duration)}</div>}
                          {block.note && height > 52 && <div style={{ fontSize: 9, color: C.textDim, fontStyle: "italic", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{block.note}</div>}
                        </div>
                        <button
                          data-edit="true"
                          onClick={e => { e.stopPropagation(); startEdit(block); }}
                          style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 3, cursor: "pointer", color: C.textDim, fontSize: 9, padding: "1px 4px", flexShrink: 0, lineHeight: 1.4, pointerEvents: "auto" }}
                          title="edit"
                        >✎</button>
                        <button
                          data-edit="true"
                          onClick={e => { e.stopPropagation(); onBlocksChange(blocks.filter(b => b.id !== block.id)); }}
                          style={{ background: "none", border: "none", borderRadius: 3, cursor: "pointer", color: C.textDim, fontSize: 10, padding: "1px 3px", flexShrink: 0, lineHeight: 1.4, pointerEvents: "auto" }}
                          title="remove"
                        >✕</button>
                      </div>
                      {height > 48 && (
                        <div style={{ fontSize: 8, color: C.textDim, fontFamily: "monospace", marginTop: "auto" }}>
                          {block.time} — {minutesToTimeStr(startMins + duration)}
                        </div>
                      )}
                    </div>

                    <div
                      data-resize="true"
                      onMouseDown={e => onResizeMouseDown(e, block.id)}
                      style={{
                        position: "absolute", bottom: 0, left: 0, right: 0, height: 8,
                        cursor: "ns-resize", display: "flex", alignItems: "center", justifyContent: "center",
                        background: "transparent", borderRadius: "0 0 4px 4px",
                        pointerEvents: "auto",
                      }}
                    >
                      <div style={{ width: 24, height: 2, background: tag.color, opacity: .4, borderRadius: 1 }} />
                    </div>
                  </div>

                  {isEditing && (
                    <div style={{
                      position: "absolute", top, left: "calc(100% + 8px)",
                      width: 240, zIndex: 100,
                      background: C.card, border: `1px solid ${C.accent}40`,
                      borderRadius: 6, padding: 12,
                      boxShadow: "0 8px 24px #00000080",
                      pointerEvents: "auto",
                    }}>
                      <div style={{ fontSize: 9, color: C.accent, letterSpacing: 1, marginBottom: 10 }}>EDIT BLOCK</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <input autoFocus value={editValues.label} onChange={e => setEditValues({ ...editValues, label: e.target.value })}
                          onKeyDown={e => e.key === "Enter" && saveEdit(block.id)}
                          placeholder="label..." style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "6px 8px", fontSize: 12, outline: "none", width: "100%" }} />
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ fontSize: 10, color: C.textDim }}>tag:</span>
                          <TagSelector tags={tags} value={editValues.tag} onChange={v => setEditValues({ ...editValues, tag: v })} onCreateTag={onPersistTags} />
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ fontSize: 10, color: C.textDim }}>duration:</span>
                          <input type="number" value={editValues.duration} min={SNAP_MINUTES} step={SNAP_MINUTES}
                            onChange={e => setEditValues({ ...editValues, duration: Math.max(SNAP_MINUTES, parseInt(e.target.value) || DEFAULT_DURATION) })}
                            style={{ width: 64, background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "5px 7px", fontSize: 12, outline: "none" }} />
                          <span style={{ fontSize: 10, color: C.textDim }}>min</span>
                        </div>
                        <textarea value={editValues.note} onChange={e => setEditValues({ ...editValues, note: e.target.value })}
                          placeholder="note (optional)..." rows={2}
                          style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "6px 8px", fontSize: 11, outline: "none", resize: "none", lineHeight: 1.5 }} />
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => saveEdit(block.id)} style={{ flex: 1, background: C.accent, border: "none", color: "#031a20", borderRadius: 4, padding: "7px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ flex: 1, background: "none", border: `1px solid ${C.border}`, color: C.textDim, borderRadius: 4, padding: "7px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Google Calendar read-only events */}
          {gcalEvents.map(ev => {
            const start = new Date(ev.start);
            const end = new Date(ev.end);
            const startMins = start.getHours() * 60 + start.getMinutes();
            const endMins = end.getHours() * 60 + end.getMinutes();
            const duration = Math.max(15, endMins - startMins);
            const top = minutesToPx(startMins - DAY_START_HOUR * 60);
            const height = Math.max(minutesToPx(duration), 20);
            if (startMins < DAY_START_HOUR * 60 || startMins > DAY_END_HOUR * 60) return null;
            return (
              <div key={ev.id} title={ev.title} style={{
                position: "absolute", top, left: 2, right: 2, height,
                background: "#38bdf810", border: "1px dashed #38bdf840",
                borderLeft: "3px solid #38bdf8",
                borderRadius: 4, padding: "3px 6px",
                pointerEvents: "none", zIndex: 0, overflow: "hidden",
              }}>
                <div style={{ fontSize: 10, color: "#38bdf8", opacity: .8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  📅 {ev.title}
                </div>
              </div>
            );
          })}

          {addingAt !== null && (
            <div style={{
              position: "absolute",
              top: minutesToPx(addingAt - DAY_START_HOUR * 60),
              left: 8, right: 8, zIndex: 50,
              background: C.card, border: `1px solid ${C.accent}50`,
              borderRadius: 6, padding: 12,
              boxShadow: "0 8px 24px #00000080",
            }}>
              <div style={{ fontSize: 9, color: C.accent, letterSpacing: 1, marginBottom: 8 }}>NEW BLOCK AT {minutesToTimeStr(addingAt)}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <input autoFocus value={newLabel} onChange={e => setNewLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addBlock(); if (e.key === "Escape") setAddingAt(null); }}
                  placeholder="what is it..."
                  style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "7px 10px", fontSize: 13, outline: "none", minWidth: 120 }} />
                <TagSelector tags={tags} value={newTag} onChange={setNewTag} onCreateTag={onPersistTags} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={addBlock} style={{ background: C.accent, border: "none", color: "#031a20", borderRadius: 4, padding: "7px 16px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Add</button>
                <button onClick={() => setAddingAt(null)} style={{ background: "none", border: `1px solid ${C.border}`, color: C.textDim, borderRadius: 4, padding: "7px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* CHANGE 1: spacer div removed — was creating double-height gap below timeline */}
    </div>
  );
}

// ── Account: export + delete ──────────────────────────────────────────
function AccountModal({ session, onClose }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const uid = session.user.id;

  async function exportData() {
    setBusy("export"); setMsg("");
    try {
      const out = { exported_at: new Date().toISOString(), account_email: session.user.email };
      for (const table of ["user_data", "archive", "events", "journal_entries"]) {
        const { data, error } = await supabase.from(table).select("*").eq("user_id", uid);
        if (error) throw error;
        out[table] = data;
      }
      const files = await supabase.storage.from(DROP_BUCKET).list(uid);
      out.uploaded_files = (files.data || []).map(f => f.name);
      const receipts = await supabase.storage.from(RECEIPT_BUCKET).list(uid);
      out.uploaded_receipts = (receipts.data || []).map(f => f.name);
      const blob = new Blob([JSON.stringify(out, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${APP_NAME.toLowerCase()}-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setMsg("Your export has downloaded.");
    } catch (e) {
      setMsg("Couldn't export right now. Please try again, or email " + CONTACT_EMAIL + ".");
    }
    setBusy("");
  }

  async function deleteAccount() {
    setBusy("delete"); setMsg("");
    try {
      const res = await fetch("/api/delete-account", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ confirm: "DELETE" }),
      });
      if (!res.ok) throw new Error("failed");
      Object.keys(localStorage).filter(k => k.startsWith("flux_")).forEach(k => localStorage.removeItem(k));
      await supabase.auth.signOut();
    } catch (e) {
      setMsg("Couldn't delete your account. Nothing was changed. Please try again, or email " + CONTACT_EMAIL + ".");
      setBusy("");
    }
  }

  const btn = { border: "1px solid " + C.border, background: "none", color: C.text, borderRadius: 6, padding: "10px 16px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 24, width: "100%", maxWidth: 420, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, letterSpacing: 1, color: C.text }}>YOUR ACCOUNT</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 20 }}>{session.user.email}</div>

        <div style={{ fontSize: 11, letterSpacing: 2, color: C.textDim, marginBottom: 8 }}>EXPORT</div>
        <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.6, marginBottom: 10 }}>Download a copy of everything in your account as a file.</div>
        <button onClick={exportData} disabled={!!busy} style={{ ...btn, opacity: busy ? .6 : 1 }}>{busy === "export" ? "preparing..." : "Export my data"}</button>

        <div style={{ borderTop: `1px solid ${C.border}`, margin: "24px 0 18px" }} />

        <div style={{ fontSize: 11, letterSpacing: 2, color: "#ff6b6b", marginBottom: 8 }}>DELETE ACCOUNT</div>
        <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.6, marginBottom: 10 }}>
          This permanently deletes your account and all your entries, archive, events, and uploaded files. It can't be undone. Consider exporting first.
        </div>
        <div style={{ fontSize: 11, color: C.textDim, marginBottom: 6 }}>Type DELETE to confirm</div>
        <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="DELETE" autoComplete="off"
          style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", marginBottom: 10, outline: "none" }} />
        <button onClick={deleteAccount} disabled={confirmText !== "DELETE" || !!busy}
          style={{ ...btn, borderColor: "#ff6b6b", color: "#ff6b6b", opacity: confirmText === "DELETE" && !busy ? 1 : .4, cursor: confirmText === "DELETE" && !busy ? "pointer" : "not-allowed" }}>
          {busy === "delete" ? "deleting..." : "Delete my account"}
        </button>

        {msg && <div style={{ marginTop: 16, fontSize: 12, color: C.textMid, lineHeight: 1.6 }}>{msg}</div>}
        <div style={{ marginTop: 18, fontSize: 11, color: C.textDim, lineHeight: 1.6 }}>Trouble? Use the <a href="/data-request.html" target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>request form</a> or write to <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: C.accent }}>{CONTACT_EMAIL}</a></div>
      </div>
    </div>
  );
}

// ── Early Access Banner ───────────────────────────────────────────────
function EarlyAccessBanner() {
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (localStorage.getItem("flux_banner_dismissed")) { setVisible(false); setDismissed(true); }
  }, []);
  function dismiss() {
    setVisible(false);
    localStorage.setItem("flux_banner_dismissed", "true");
    setTimeout(() => setDismissed(true), 400);
  }
  if (dismissed) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, left: 24, zIndex: 999, maxWidth: 300, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(12px)", transition: "opacity .35s ease, transform .35s ease", pointerEvents: visible ? "auto" : "none" }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.accent}`, borderRadius: 6, padding: "12px 14px", boxShadow: "0 4px 20px #00000050" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: C.accent, letterSpacing: 1, marginBottom: 5 }}>🚧 EARLY ACCESS</div>
            <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.6 }}>{APP_NAME} is still being built. Things may shift, break, or get better without warning.</div>
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ fontSize: 10, color: C.textDim, marginTop: 6, display: "block", textDecoration: "none" }}>{CONTACT_EMAIL}</a>
          </div>
          <button onClick={dismiss} style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, fontSize: 14, padding: "0 2px", flexShrink: 0 }}>✕</button>
        </div>
      </div>
    </div>
  );
}






// ── Late Night Prompt ─────────────────────────────────────────────────
function LateNightPrompt({ onChoose }) {
  const now = new Date();
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  const todayLabel = now.toLocaleDateString("en-US", { weekday: "long", month: "numeric", day: "numeric" });
  const yesterdayLabel = yesterday.toLocaleDateString("en-US", { weekday: "long", month: "numeric", day: "numeric" });
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "#000000bb", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, maxWidth: 380, width: "100%", padding: "32px 28px", boxShadow: "0 24px 80px #00000080" }}>
        <div style={{ fontSize: 11, color: C.textDim, letterSpacing: 2, marginBottom: 12 }}>🌙 IT'S PAST MIDNIGHT</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 2, color: C.text, marginBottom: 8 }}>Which day is this for?</div>
        <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.7, marginBottom: 28 }}>Your day doesn't end at midnight — archive whenever you're actually done.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={() => onChoose("yesterday")} style={{ background: C.accentDim, border: `1px solid ${C.accent}50`, color: C.text, borderRadius: 6, padding: "14px 18px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left", lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, marginBottom: 3 }}>{yesterdayLabel} — still finishing up</div>
            <div style={{ fontSize: 11, color: C.textDim }}>keep today's date, archive when you're done</div>
          </button>
          <button onClick={() => onChoose("today")} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: "14px 18px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left", lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, marginBottom: 3 }}>{todayLabel} — starting fresh</div>
            <div style={{ fontSize: 11, color: C.textDim }}>roll any undone tasks and begin a new day</div>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Receipts: photos / PDFs attached to a task ────────────────────────
const RECEIPT_BUCKET    = "receipts";
const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;
const RECEIPT_MAX_EACH  = 5;

// Phone photos are huge; shrink to a readable size before uploading.
async function shrinkImage(file, maxEdge = 1800, quality = 0.82) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", quality));
    return blob && blob.size < file.size ? blob : file;
  } catch { return file; }
}

async function openReceipt(path) {
  const w = window.open("", "_blank"); // open synchronously so phones don't block the popup
  const { data } = await supabase.storage.from(RECEIPT_BUCKET).createSignedUrl(path, 120);
  if (data?.signedUrl && w) w.location = data.signedUrl; else if (w) w.close();
}

function removeReceiptFiles(receipts) {
  const paths = (receipts || []).map(r => r.path);
  if (paths.length) supabase.storage.from(RECEIPT_BUCKET).remove(paths);
}

// onChange receives a function (list => newList) so a slow upload never overwrites other edits.
function ReceiptBar({ receipts = [], userId, onChange, readOnly = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function pick(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setBusy(true); setErr("");
    const added = [];
    for (const f of picked) {
      if (receipts.length + added.length >= RECEIPT_MAX_EACH) { setErr(`up to ${RECEIPT_MAX_EACH} per task`); break; }
      const isPdf = f.type === "application/pdf";
      if (!isPdf && !f.type.startsWith("image/")) { setErr("photos or PDFs only"); continue; }
      const body = isPdf ? f : await shrinkImage(f);
      if (body.size > RECEIPT_MAX_BYTES) { setErr("that file is over 5 MB"); continue; }
      const type = body.type || f.type;
      const ext = isPdf ? "pdf" : type === "image/jpeg" ? "jpg" : (type.split("/")[1] || "img").slice(0, 5);
      const path = `${userId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from(RECEIPT_BUCKET).upload(path, body, { contentType: type });
      if (error) { setErr("couldn't upload, try again"); continue; }
      added.push({ path, name: f.name.slice(0, 60), type, size: body.size });
    }
    if (added.length) onChange(list => [...(list || []), ...added]);
    setBusy(false);
  }

  function remove(r) {
    removeReceiptFiles([r]);
    onChange(list => (list || []).filter(x => x.path !== r.path));
  }

  if (readOnly && !receipts.length) return null;
  const chip = { display: "inline-flex", alignItems: "center", gap: 4, background: C.surface, border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 12, padding: "2px 8px", fontSize: 10, cursor: "pointer", fontFamily: "inherit" };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 5 }}>
      {receipts.map((r, i) => (
        <span key={r.path} style={chip}>
          <span onClick={() => openReceipt(r.path)} title={r.name}>{r.type === "application/pdf" ? "📄" : "🧾"} receipt {i + 1}</span>
          {!readOnly && <span onClick={() => remove(r)} title="remove" style={{ color: C.textDim }}>✕</span>}
        </span>
      ))}
      {!readOnly && receipts.length < RECEIPT_MAX_EACH && (
        <>
          <input ref={inputRef} type="file" accept="image/*,application/pdf" multiple style={{ display: "none" }} onChange={pick} />
          <button onClick={() => inputRef.current?.click()} disabled={busy} style={{ ...chip, borderStyle: "dashed", color: busy ? C.accent : C.textDim }}>
            {busy ? "saving…" : "📎 receipt"}
          </button>
        </>
      )}
      {err && <span style={{ fontSize: 10, color: "#ff6b6b" }}>{err}</span>}
    </div>
  );
}

// ── Task Drawer ───────────────────────────────────────────────────────
function TaskDrawer({ tasks, onTasksChange, userId }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [schedDate, setSchedDate] = useState("");
  const inputRef = useRef();
  const remaining = tasks.filter(t => !t.done && (!t.scheduledFor || t.scheduledFor <= todayKey())).length;
  const doneToday = tasks.filter(t => t.done).length;
  const pending = tasks.filter(t => !t.done && (!t.scheduledFor || t.scheduledFor <= todayKey()));
  const scheduled = tasks.filter(t => !t.done && t.scheduledFor && t.scheduledFor > todayKey()).sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
  const done = tasks.filter(t => t.done);
  function addTask() {
    const label = input.trim(); if (!label) return;
    onTasksChange([...tasks, { id: genId(), label, done: false, addedAt: nowStamp(), scheduledFor: schedDate || null }]);
    setInput(""); setSchedDate(""); inputRef.current?.focus();
  }
  function toggleTask(id) { onTasksChange(tasks.map(t => t.id !== id ? t : { ...t, done: !t.done, doneAt: !t.done ? nowStamp() : undefined })); }
  function deleteTask(id) { removeReceiptFiles(tasks.find(t => t.id === id)?.receipts); onTasksChange(tasks.filter(t => t.id !== id)); }
  const setReceipts = (id) => (fn) => onTasksChange(prev => prev.map(t => t.id !== id ? t : { ...t, receipts: fn(t.receipts) }));
  return (
    <div style={{ marginBottom: 20 }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", background: C.card, border: `1px solid ${C.border}`, borderRadius: open ? "6px 6px 0 0" : "6px", cursor: "pointer", userSelect: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: C.textMid, letterSpacing: .5 }}>▸ tasks</span>
          <span style={{ fontSize: 11, color: C.textDim }}>
            {remaining > 0 ? <><span style={{ color: C.text }}>{remaining}</span> remaining</> : <span style={{ color: "#10b981" }}>all clear</span>}
            {doneToday > 0 && <span style={{ marginLeft: 8, color: "#10b981" }}>· {doneToday} done today</span>}
            {scheduled.length > 0 && <span style={{ marginLeft: 8, color: C.textDim }}>· {scheduled.length} scheduled</span>}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span onClick={e => { e.stopPropagation(); setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }} style={{ fontSize: 16, color: C.textDim, padding: "0 4px", lineHeight: 1 }}>+</span>
          <span style={{ fontSize: 10, color: C.textDim, display: "inline-block", transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .2s" }}>▶</span>
        </div>
      </div>
      {open && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: "none", borderRadius: "0 0 6px 6px", padding: "12px 14px" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && addTask()} placeholder="add a task, hit enter..."
              style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "7px 10px", fontSize: 13, outline: "none", minWidth: 160 }} />
            <input type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)}
              style={{ background: C.card, border: `1px solid ${C.border}`, color: schedDate ? C.text : C.textDim, borderRadius: 4, padding: "7px 8px", fontSize: 12, outline: "none", fontFamily: "inherit" }} />
            <button onClick={addTask} style={{ background: C.accentDim, border: `1px solid ${C.accent}40`, color: C.accent, borderRadius: 4, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>add</button>
          </div>
          {pending.length === 0 && done.length === 0 && scheduled.length === 0 && <div style={{ fontSize: 12, color: C.textDim, padding: "8px 0", textAlign: "center" }}>nothing here yet</div>}
          {pending.length === 0 && done.length > 0 && <div style={{ fontSize: 12, color: "#10b981", padding: "6px 0", textAlign: "center" }}>everything done 🎉</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {pending.map(task => (
              <div key={task.id} className="task-row" style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 8px", borderRadius: 4, background: C.card, border: `1px solid ${C.border}` }}>
                <div onClick={() => toggleTask(task.id)} style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${C.border}`, cursor: "pointer", flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#10b981"} onMouseLeave={e => e.currentTarget.style.borderColor = C.border} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: C.text }}>{task.label}</span>
                  <ReceiptBar receipts={task.receipts} userId={userId} onChange={setReceipts(task.id)} />
                </div>
                {task.addedAt && <span style={{ fontSize: 10, color: C.textDim }}>{task.addedAt}</span>}
                <button onClick={() => deleteTask(task.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, fontSize: 11, padding: "1px 3px", opacity: 0, transition: "opacity .15s" }} className="task-del">✕</button>
              </div>
            ))}
          </div>
          {scheduled.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 6 }}>SCHEDULED</div>
              {scheduled.map(task => (
                <div key={task.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 4, marginBottom: 3, background: C.card, border: `1px solid ${C.border}`, opacity: .6 }}>
                  <span style={{ fontSize: 10, color: "#38bdf8", minWidth: 60 }}>{formatEventDate(task.scheduledFor)}</span>
                  <span style={{ flex: 1, fontSize: 12, color: C.textMid }}>{task.label}</span>
                  <button onClick={() => deleteTask(task.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, fontSize: 11 }}>✕</button>
                </div>
              ))}
            </div>
          )}
          {done.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 6 }}>DONE TODAY</div>
              {done.map(task => (
                <div key={task.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 4, opacity: .45 }}>
                  <div onClick={() => toggleTask(task.id)} style={{ width: 16, height: 16, borderRadius: 3, border: "1.5px solid #10b981", background: "#10b98120", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 10, color: "#10b981" }}>✓</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 12, color: C.textDim, textDecoration: "line-through" }}>{task.label}</span>
                    <ReceiptBar receipts={task.receipts} userId={userId} onChange={setReceipts(task.id)} />
                  </div>
                  <span style={{ fontSize: 10, color: C.textDim }}>{task.doneAt}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.textDim, lineHeight: 1.6 }}>archive day → snapshot saved · done cleared · undone rolls to tomorrow</div>
        </div>
      )}
    </div>
  );
}

// ── Upcoming Events Drawer ────────────────────────────────────────────
function UpcomingDrawer({ events }) {
  const [open, setOpen] = useState(false);
  const upcoming = events.filter(e => isFuture(e.date)).sort((a, b) => a.date.localeCompare(b.date));
  const todayEvents = upcoming.filter(e => isToday(e.date));
  const futureEvents = upcoming.filter(e => !isToday(e.date)).slice(0, 5);
  const next = upcoming.find(e => !isToday(e.date));
  const thisWeek = upcoming.filter(e => { const diff = Math.round((dateFromLocalKey(e.date) - new Date()) / 86400000); return diff > 0 && diff <= 7; }).length;
  return (
    <div style={{ marginBottom: 20 }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", background: C.card, border: `1px solid ${C.border}`, borderRadius: open ? "6px 6px 0 0" : "6px", cursor: "pointer", userSelect: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: C.textMid, letterSpacing: .5 }}>▸ upcoming</span>
          <span style={{ fontSize: 11, color: C.textDim }}>
            {todayEvents.length > 0 && <span style={{ color: C.accent }}>{todayEvents.length} today</span>}
            {todayEvents.length > 0 && thisWeek > 0 && <span> · </span>}
            {thisWeek > 0 && <span>{thisWeek} this week</span>}
            {todayEvents.length === 0 && thisWeek === 0 && next && <span>next: <span style={{ color: C.text }}>{next.title}</span> {formatEventDate(next.date)}{next.time ? ` ${next.time}` : ""}</span>}
            {upcoming.length === 0 && <span style={{ color: C.textDim }}>nothing scheduled</span>}
          </span>
        </div>
        <span style={{ fontSize: 10, color: C.textDim, display: "inline-block", transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .2s" }}>▶</span>
      </div>
      {open && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: "none", borderRadius: "0 0 6px 6px", padding: "12px 14px" }}>
          {todayEvents.length > 0 && <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: C.accent, letterSpacing: 1, marginBottom: 7 }}>TODAY</div>
            {todayEvents.map(e => <div key={e.id} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 8px", background: C.accentDim, border: `1px solid ${C.accent}30`, borderRadius: 4, marginBottom: 4 }}>
              {e.time && <span style={{ fontSize: 11, color: C.accent, minWidth: 50 }}>{e.time}</span>}
              <span style={{ fontSize: 13, color: C.text }}>{e.title}</span>
              {e.note && <span style={{ fontSize: 11, color: C.textDim, fontStyle: "italic" }}>— {e.note}</span>}
            </div>)}
          </div>}
          {futureEvents.length > 0 && <div>
            <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 7 }}>COMING UP</div>
            {futureEvents.map(e => <div key={e.id} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "5px 8px", marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: "#38bdf8", minWidth: 70 }}>{formatEventDate(e.date)}{e.time ? ` ${e.time}` : ""}</span>
              <span style={{ fontSize: 12, color: C.text }}>{e.title}</span>
              {e.note && <span style={{ fontSize: 11, color: C.textDim, fontStyle: "italic" }}>— {e.note}</span>}
            </div>)}
          </div>}
          {upcoming.length === 0 && <div style={{ fontSize: 12, color: C.textDim, textAlign: "center", padding: "8px 0" }}>nothing scheduled — add events in the Calendar tab</div>}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.textDim }}>manage events in the Calendar tab</div>
        </div>
      )}
    </div>
  );
}

// ── Calendar View ─────────────────────────────────────────────────────
function CalendarView({ events, onEventsChange }) {
  const [newEvent, setNewEvent]   = useState({ title: "", date: "", time: "", note: "" });
  const [adding, setAdding]       = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [calView, setCalView]     = useState("list"); // "list" | "grid"
  const [gridMonth, setGridMonth] = useState(() => {
    const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() };
  });

  const sorted  = [...events].sort((a, b) => a.date.localeCompare(b.date));
  const past    = sorted.filter(e => e.date < todayKey());
  const present = sorted.filter(e => isFuture(e.date));

  function addEvent() {
    if (!newEvent.title.trim() || !newEvent.date) return;
    onEventsChange([...events, { ...newEvent, id: genId() }]);
    setNewEvent({ title: "", date: "", time: "", note: "" }); setAdding(false);
  }
  function deleteEvent(id) { onEventsChange(events.filter(e => e.id !== id)); }
  function startEdit(e) { setEditingId(e.id); setEditValues({ title: e.title, date: e.date, time: e.time || "", note: e.note || "" }); }
  function saveEdit() { onEventsChange(events.map(e => e.id === editingId ? { ...e, ...editValues } : e)); setEditingId(null); }

  const editForm = (
    <div style={{ background: C.surface, border: `1px solid ${C.accent}40`, borderRadius: 6, padding: 12, marginTop: 6 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input autoFocus value={editValues.title} onChange={e => setEditValues({ ...editValues, title: e.target.value })} onKeyDown={e => e.key === "Enter" && saveEdit()}
          style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "7px 10px", fontSize: 12, outline: "none" }} />
        <div style={{ display: "flex", gap: 8 }}>
          <input type="date" value={editValues.date} onChange={e => setEditValues({ ...editValues, date: e.target.value })}
            style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "7px 10px", fontSize: 12, outline: "none", fontFamily: "inherit" }} />
          <input type="time" value={editValues.time} onChange={e => setEditValues({ ...editValues, time: e.target.value })}
            style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "7px 10px", fontSize: 12, outline: "none", fontFamily: "inherit" }} />
        </div>
        <input placeholder="note (optional)" value={editValues.note} onChange={e => setEditValues({ ...editValues, note: e.target.value })}
          style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "7px 10px", fontSize: 12, outline: "none" }} />
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={saveEdit} style={{ background: C.accent, border: "none", color: "#031a20", borderRadius: 4, padding: "6px 16px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>save</button>
          <button onClick={() => setEditingId(null)} style={{ background: "none", border: `1px solid ${C.border}`, color: C.textDim, borderRadius: 4, padding: "6px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>cancel</button>
        </div>
      </div>
    </div>
  );

  const grouped = present.reduce((acc, e) => {
    const month = dateFromLocalKey(e.date).toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (!acc[month]) acc[month] = []; acc[month].push(e); return acc;
  }, {});

  // Grid helpers
  function gridDays() {
    const { year, month } = gridMonth;
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    const days  = [];
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  }
  function gridKey(d) { return d ? localDateKey(d) : null; }
  const eventsByDate = events.reduce((acc, e) => { if (!acc[e.date]) acc[e.date] = []; acc[e.date].push(e); return acc; }, {});
  const gridMonthLabel = new Date(gridMonth.year, gridMonth.month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  function prevMonth() { setGridMonth(({ year, month }) => month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 }); }
  function nextMonth() { setGridMonth(({ year, month }) => month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 }); }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: 3, color: C.textMid }}>CALENDAR</div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{present.length} upcoming · {past.length} past</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {/* View toggle */}
          <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 4, overflow: "hidden" }}>
            {["list", "grid"].map(v => (
              <button key={v} onClick={() => setCalView(v)} style={{ background: calView === v ? C.accent : "none", border: "none", color: calView === v ? "#fff" : C.textDim, padding: "5px 12px", fontSize: 10, cursor: "pointer", fontFamily: "inherit", letterSpacing: 1 }}>
                {v === "list" ? "≡" : "▦"}
              </button>
            ))}
          </div>
          <button onClick={() => setAdding(!adding)} style={{ background: adding ? "none" : C.accent, border: adding ? `1px solid ${C.border}` : "none", color: adding ? C.textDim : "#fff", borderRadius: 4, padding: "8px 18px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
            {adding ? "cancel" : "+ add event"}
          </button>
        </div>
      </div>

      {/* Add form */}
      {adding && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 12 }}>NEW EVENT</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input autoFocus placeholder="what is it..." value={newEvent.title} onChange={e => setNewEvent({ ...newEvent, title: e.target.value })} onKeyDown={e => e.key === "Enter" && addEvent()}
              style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "8px 10px", fontSize: 13, outline: "none" }} />
            <div style={{ display: "flex", gap: 10 }}>
              <input type="date" value={newEvent.date} onChange={e => setNewEvent({ ...newEvent, date: e.target.value })} style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "8px 10px", fontSize: 12, outline: "none", fontFamily: "inherit" }} />
              <input type="time" value={newEvent.time} onChange={e => setNewEvent({ ...newEvent, time: e.target.value })} style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "8px 10px", fontSize: 12, outline: "none", fontFamily: "inherit" }} />
            </div>
            <input placeholder="note (optional)" value={newEvent.note} onChange={e => setNewEvent({ ...newEvent, note: e.target.value })} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "8px 10px", fontSize: 12, outline: "none" }} />
            <button onClick={addEvent} style={{ background: C.accent, border: "none", color: "#031a20", borderRadius: 4, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", alignSelf: "flex-start" }}>Add Event</button>
          </div>
        </div>
      )}

      {/* GRID VIEW */}
      {calView === "grid" && (
        <div>
          {/* Month nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <button onClick={prevMonth} style={{ background: "none", border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 4, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>‹</button>
            <div style={{ fontSize: 11, letterSpacing: 2, color: C.textMid, textTransform: "uppercase" }}>{gridMonthLabel}</div>
            <button onClick={nextMonth} style={{ background: "none", border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 4, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>›</button>
          </div>

          {/* Day labels */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 2 }}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: 9, color: C.textDim, letterSpacing: 1, padding: "4px 0" }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {gridDays().map((d, i) => {
              const key  = gridKey(d);
              const evs  = key ? (eventsByDate[key] || []) : [];
              const isTd = key === todayKey();
              const isPast = key && key < todayKey();
              return (
                <div key={i} style={{ minHeight: 64, background: d ? (isTd ? C.accentDim : C.card) : "transparent", border: d ? `1px solid ${isTd ? C.accent + "60" : C.border}` : "none", borderRadius: 4, padding: "5px 6px", position: "relative" }}>
                  {d && (
                    <>
                      <div style={{ fontSize: 11, color: isTd ? C.accent : isPast ? C.textDim : C.text, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1, lineHeight: 1, marginBottom: 3, opacity: isPast ? 0.5 : 1 }}>{d.getDate()}</div>
                      {evs.slice(0, 2).map(e => (
                        <div key={e.id} title={e.title} style={{ fontSize: 9, color: "#38bdf8", background: "#0c2233", border: "1px solid #38bdf820", borderRadius: 2, padding: "1px 4px", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {e.time && <span style={{ opacity: .6 }}>{e.time.slice(0,5)} </span>}{e.title}
                        </div>
                      ))}
                      {evs.length > 2 && <div style={{ fontSize: 8, color: C.textDim }}>+{evs.length - 2} more</div>}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Events on grid — click-to-delete below */}
          {Object.entries(eventsByDate)
            .filter(([k]) => k.startsWith(`${gridMonth.year}-${String(gridMonth.month + 1).padStart(2,"0")}`))
            .sort(([a],[b]) => a.localeCompare(b))
            .map(([date, evs]) => (
              <div key={date} style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 6 }}>
                  {dateFromLocalKey(date).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
                </div>
                {evs.map(e => (
                  <div key={e.id} style={{ marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", background: C.card, border: `1px solid ${C.border}`, borderLeft: "3px solid #38bdf8", borderRadius: editingId === e.id ? "4px 4px 0 0" : 4 }}>
                      <div style={{ flex: 1, fontSize: 12, color: C.text }}>{e.title}{e.time && <span style={{ color: "#38bdf8", marginLeft: 8, fontSize: 10 }}>{e.time}</span>}</div>
                      <button onClick={() => editingId === e.id ? setEditingId(null) : startEdit(e)} style={{ background: "none", border: `1px solid ${C.border}`, cursor: "pointer", color: C.textDim, fontSize: 10, padding: "2px 8px", borderRadius: 3, fontFamily: "inherit" }}>✎</button>
                      <button onClick={() => deleteEvent(e.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, fontSize: 11 }}>✕</button>
                    </div>
                    {editingId === e.id && editForm}
                  </div>
                ))}
              </div>
            ))}
        </div>
      )}

      {/* LIST VIEW */}
      {calView === "list" && (
        <div>
          {present.length === 0 && <div style={{ textAlign: "center", padding: "40px 0", color: C.textDim, fontSize: 13 }}>nothing scheduled yet</div>}
          {Object.entries(grouped).map(([month, evs]) => (
            <div key={month} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 2, marginBottom: 10, textTransform: "uppercase" }}>{month}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {evs.map(e => {
                  const d = dateFromLocalKey(e.date); const isItToday = isToday(e.date);
                  return (
                    <div key={e.id}>
                      <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "10px 14px", background: isItToday ? C.accentDim : C.card, border: `1px solid ${isItToday ? C.accent + "50" : C.border}`, borderLeft: `3px solid ${isItToday ? C.accent : "#38bdf8"}`, borderRadius: editingId === e.id ? "6px 6px 0 0" : 6 }}>
                        <div style={{ textAlign: "center", minWidth: 32 }}>
                          <div style={{ fontSize: 18, fontFamily: "'Bebas Neue',sans-serif", color: isItToday ? C.accent : C.text, lineHeight: 1 }}>{d.getDate()}</div>
                          <div style={{ fontSize: 9, color: C.textDim, textTransform: "uppercase" }}>{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, color: C.text }}>{e.title}</div>
                          <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                            {e.time && <span style={{ color: "#38bdf8", marginRight: 8 }}>{e.time}</span>}
                            {e.note && <span style={{ fontStyle: "italic" }}>{e.note}</span>}
                          </div>
                        </div>
                        <button onClick={() => editingId === e.id ? setEditingId(null) : startEdit(e)} style={{ background: "none", border: `1px solid ${C.border}`, cursor: "pointer", color: C.textDim, fontSize: 10, padding: "2px 8px", borderRadius: 3, fontFamily: "inherit" }}>✎</button>
                        <button onClick={() => deleteEvent(e.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, fontSize: 12, padding: "2px 4px" }}>✕</button>
                      </div>
                      {editingId === e.id && editForm}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {past.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11, color: C.textDim, cursor: "pointer", letterSpacing: 1, listStyle: "none", marginBottom: 10 }}>▸ {past.length} past event{past.length !== 1 ? "s" : ""}</summary>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, opacity: .4 }}>
                {past.reverse().map(e => (
                  <div key={e.id} style={{ display: "flex", gap: 10, padding: "6px 10px", borderRadius: 4 }}>
                    <span style={{ fontSize: 11, color: C.textDim, minWidth: 80 }}>{dateFromLocalKey(e.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                    <span style={{ fontSize: 12, color: C.textDim, textDecoration: "line-through" }}>{e.title}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// -- Journal Drawer -----------------------------------------------------------
function JournalDrawer({ journalInput, setJournalInput, journalInputRef, journalType, setJournalType, journalPrompt, setJournalPromptOffset, addJournalEntry, journalSaving, journalFeedback, todayJournalEntries, setJournalEntryType, toggleJournalPinned, softDeleteJournalEntry }) {
  const [open, setOpen] = useState(true);
  const count = todayJournalEntries.length;

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 12 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "12px 0", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "inherit" }}>
        <span style={{ fontSize: 12, color: C.textMid, letterSpacing: .5 }}>▸ journal</span>
        <span style={{ fontSize: 10, color: C.textDim, display: "flex", alignItems: "center", gap: 8 }}>
          {count > 0 && <span>{count} entr{count === 1 ? "y" : "ies"}</span>}
          <span style={{ display: "inline-block", transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .2s" }}>▶</span>
        </span>
      </button>

      {open && (
        <div style={{ paddingBottom: 16 }}>
          {/* Prompt */}
          <div style={{ fontSize: 11, color: C.textDim, marginBottom: 10, fontStyle: "italic" }}>{journalPrompt}</div>

          {/* Textarea input */}
          <textarea
            ref={journalInputRef}
            value={journalInput}
            onChange={e => setJournalInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && e.metaKey && addJournalEntry()}
            placeholder="what's happening right now..."
            rows={3}
            style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "10px 12px", fontSize: 12, outline: "none", fontFamily: "inherit", resize: "none", lineHeight: 1.7, marginBottom: 8 }}
          />

          {/* Type + actions row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {JOURNAL_TYPES.map(type => (
                <button key={type} onClick={() => setJournalType(type)}
                  style={{ background: journalType === type ? C.accentDim : "none", border: journalType === type ? `1px solid ${C.accent}` : `1px solid ${C.border}`, color: journalType === type ? C.accent : C.textDim, borderRadius: 999, padding: "2px 9px", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                  {JOURNAL_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setJournalPromptOffset(n => n + 1)}
                style={{ background: "none", border: `1px solid ${C.border}`, color: C.textDim, borderRadius: 4, padding: "5px 10px", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                new prompt
              </button>
              <button onClick={addJournalEntry} disabled={journalSaving || !journalInput.trim()}
                style={{ background: C.accentDim, border: `1px solid ${C.accent}40`, color: C.accent, borderRadius: 4, padding: "5px 14px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", opacity: journalSaving || !journalInput.trim() ? 0.5 : 1 }}>
                {journalSaving ? "saving..." : "add →"}
              </button>
            </div>
          </div>

          {journalFeedback && (
            <div style={{ fontSize: 11, marginBottom: 8, color: journalFeedback.type === "ok" ? "#10b981" : "#ef4444" }}>{journalFeedback.message}</div>
          )}

          {count === 0 && (
            <div style={{ fontSize: 12, color: C.textDim, padding: "12px 0", textAlign: "center", fontStyle: "italic" }}>
              nothing yet — add your first thought above
            </div>
          )}

          {[...todayJournalEntries].reverse().map(entry => (
            <div key={entry.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 4, padding: "8px 10px", marginBottom: 6 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontSize: 10, color: C.textDim, minWidth: 48, marginTop: 2 }}>{formatEntryTime(entry.created_at)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6, marginBottom: 6 }}>{entry.text}</div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {JOURNAL_TYPES.map(type => (
                      <button key={type} onClick={() => setJournalEntryType(entry.id, type)}
                        style={{ background: entry.type === type ? C.accentDim : "none", border: entry.type === type ? `1px solid ${C.accent}` : `1px solid ${C.border}`, color: entry.type === type ? C.accent : C.textDim, borderRadius: 999, padding: "2px 7px", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>
                        {JOURNAL_TYPE_LABELS[type]}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  <button onClick={() => toggleJournalPinned(entry.id, !entry.pinned)}
                    style={{ background: "none", border: "none", color: entry.pinned ? C.accent : C.textDim, cursor: "pointer", fontSize: 10, fontFamily: "inherit" }}>
                    {entry.pinned ? "pinned" : "pin"}
                  </button>
                  <button onClick={() => softDeleteJournalEntry(entry.id)}
                    style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 10, fontFamily: "inherit" }}>
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -- The Download Drawer (end-of-day debrief) ---------------------------------
function DownloadDrawer({ dayNote, setDayNote, wins, setWins, hard, setHard, completedTasksCount, carryoversCount, followUpOrStuckCount, archiveTime, setArchiveTime, saveToday, openArchiveModal }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 6 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "12px 0", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "inherit" }}>
        <span style={{ fontSize: 12, color: C.textMid, letterSpacing: .5 }}>▸ the download</span>
        <span style={{ fontSize: 10, color: C.textDim, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ display: "inline-block", transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .2s" }}>▶</span>
        </span>
      </button>

      {open && (
        <div style={{ paddingBottom: 16 }}>
          {/* Stats */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 4, padding: "8px 12px", marginBottom: 14, fontSize: 11, color: C.textMid, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span>{completedTasksCount} done</span>
            <span>{carryoversCount} open</span>
            <span>{followUpOrStuckCount} to follow up</span>
          </div>

          {/* One thing to carry forward */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, display: "block", marginBottom: 5, textTransform: "uppercase" }}>one thing to carry forward</label>
            <textarea value={dayNote} onChange={e => setDayNote(e.target.value)} placeholder="what does tomorrow need to start with?"
              style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 12, padding: "9px 12px", resize: "none", minHeight: 60, outline: "none", lineHeight: 1.6, fontFamily: "inherit" }} />
          </div>

          <details style={{ marginBottom: 12 }}>
            <summary style={{ fontSize: 11, color: C.textDim, cursor: "pointer", marginBottom: 8, userSelect: "none" }}>more (optional)</summary>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              <div>
                <label style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, display: "block", marginBottom: 4, textTransform: "uppercase" }}>what landed</label>
                <textarea value={wins} onChange={e => setWins(e.target.value)} placeholder="anything — big or small..."
                  style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 12, padding: "8px 11px", resize: "none", minHeight: 52, outline: "none", lineHeight: 1.6, fontFamily: "inherit" }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, display: "block", marginBottom: 4, textTransform: "uppercase" }}>what got in the way</label>
                <textarea value={hard} onChange={e => setHard(e.target.value)} placeholder="friction, blockers, or just life..."
                  style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 12, padding: "8px 11px", resize: "none", minHeight: 52, outline: "none", lineHeight: 1.6, fontFamily: "inherit" }} />
              </div>
            </div>
          </details>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={saveToday} style={{ background: "none", border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 4, padding: "7px 16px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>save</button>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10, color: C.textDim }}>auto-archive at</span>
              <input type="time" value={archiveTime} onChange={e => { setArchiveTime(e.target.value); localStorage.setItem("flux_archive_time", e.target.value); }}
                style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "5px 7px", fontSize: 11, outline: "none", fontFamily: "inherit" }} />
            </div>
            <button onClick={openArchiveModal} style={{ background: C.accent, border: "none", color: "#031a20", borderRadius: 4, padding: "7px 20px", fontSize: 11, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>archive day →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// -- File Drop (owner-only hidden transfer tool) --------------------------------
const DROP_OWNER_ID = "134f61b6-0d67-4ccb-9be2-f76e0b85f9d7";
const DROP_BUCKET   = "filedrop";

function FileDrop({ userId }) {
  const [files, setFiles]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg]           = useState(null);
  const inputRef = useRef(null);

  async function loadFiles() {
    const { data } = await supabase.storage.from(DROP_BUCKET).list(userId, { sortBy: { column: "created_at", order: "desc" } });
    setFiles(data || []);
  }

  useEffect(() => { loadFiles(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function upload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const path = `${userId}/${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from(DROP_BUCKET).upload(path, file);
    if (error) { setMsg("upload failed: " + error.message); }
    else { setMsg("uploaded"); await loadFiles(); }
    setUploading(false);
    setTimeout(() => setMsg(null), 2500);
  }

  async function download(name) {
    const { data } = await supabase.storage.from(DROP_BUCKET).createSignedUrl(`${userId}/${name}`, 60);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  }

  async function remove(name) {
    await supabase.storage.from(DROP_BUCKET).remove([`${userId}/${name}`]);
    setFiles(f => f.filter(x => x.name !== name));
  }

  return (
    <div style={{ maxWidth: 520, margin: "60px auto", padding: "0 24px" }}>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 3, color: C.textMid, marginBottom: 4 }}>FILE DROP</div>
      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 28 }}>personal transfer · owner only</div>

      <div
        onClick={() => inputRef.current?.click()}
        style={{ border: `2px dashed ${C.border}`, borderRadius: 8, padding: "32px 20px", textAlign: "center", cursor: "pointer", marginBottom: 24, transition: "border-color .15s" }}
        onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
        onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
      >
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.tsv,.ods,.zip,.tar,.gz" style={{ display: "none" }} onChange={upload} />
        <div style={{ fontSize: 13, color: uploading ? C.accent : C.textMid }}>
          {uploading ? "uploading…" : "click to upload a file"}
        </div>
        <div style={{ fontSize: 10, color: C.textDim, marginTop: 6 }}>csv · xlsx · xls · zip · and more</div>
      </div>

      {msg && <div style={{ fontSize: 11, color: C.accent, marginBottom: 16 }}>{msg}</div>}

      {files.length === 0 && <div style={{ fontSize: 12, color: C.textDim, textAlign: "center", padding: "20px 0" }}>no files yet</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {files.map(f => (
          <div key={f.name} style={{ display: "flex", alignItems: "center", gap: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "10px 14px" }}>
            <div style={{ flex: 1, fontSize: 12, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {f.name.replace(/^\d+_/, "")}
            </div>
            <div style={{ fontSize: 10, color: C.textDim, flexShrink: 0 }}>
              {f.metadata?.size ? (f.metadata.size / 1024).toFixed(0) + " kb" : ""}
            </div>
            <button onClick={() => download(f.name)} style={{ background: "none", border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 4, padding: "4px 10px", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>↓</button>
            <button onClick={() => remove(f.name)} style={{ background: "none", border: "none", color: C.textDim, fontSize: 12, cursor: "pointer", padding: "4px 6px" }}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// -- Auth / Landing / Sign-up / Sign-in screens --------------------------------
function AuthScreen({ onSignOut = null }) {
  // page: "landing" | "signin" | "signup" | "forgot"
  const [page, setPage] = useState("landing");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  function go(p) { setPage(p); setError(""); setSuccessMsg(""); }

  async function handleSignIn() {
    setError(""); setLoading(true);
    const { error: e } = await supabase.auth.signInWithPassword({ email, password });
    if (e) setError(e.message);
    setLoading(false);
  }

  async function handleForgot() {
    setError(""); setLoading(true);
    const { error: e } = await supabase.auth.resetPasswordForEmail(email);
    if (e) setError(e.message);
    else { setSuccessMsg("Reset link sent. Check your email."); setEmail(""); }
    setLoading(false);
  }

  async function handleSignUp() {
    setError(""); setLoading(true);
    const { error: e } = await supabase.auth.signUp({ email, password });
    if (e) setError(e.message);
    else setSuccessMsg("Account created! Check your email to confirm, then sign in.");
    setLoading(false);
  }

  const iStyle = (dis) => ({
    width: "100%", background: "#081315", border: "1px solid #1a383e",
    color: "#f0f0f2", borderRadius: 4, padding: "12px 14px", fontSize: 13,
    outline: "none", fontFamily: "'Inter',sans-serif", transition: "border-color .15s",
    opacity: dis ? 0.5 : 1, display: "block",
  });

  const FEATURES = [
    { num: "01", title: "A schedule that moves with you", desc: "Visual time blocks sized by duration. Drag to reorder when your day shifts. Time is a suggestion — structure is the point." },
    { num: "02", title: "Tasks that carry forward", desc: "Undone tasks roll to tomorrow when you archive. A quiet asterisk after a few days. A nudge, not a judgment." },
    { num: "03", title: "Your archive is a real record", desc: "When you archive your day, every time block saves with it. Went over 50 minutes on the same task three times this week? That surfaces. Coffee with your bestie every Wednesday? " + APP_NAME + " will suggest making it recurring." },
    { num: "04", title: "Tags that earn their place", desc: "Four defaults built in. Create any tag you need — use it enough and it becomes a real tracked category with its own colour." },
    { num: "05", title: "End of day debrief", desc: "Wins, hard stuff, brain dump. Three fields, one archive. Tomorrow starts clean every time." },
    { num: "06", title: "A calendar that finds you", desc: "Add an appointment months out. It surfaces in your day when it matters. You never have to remember to check." },
    { num: "07", title: "Patterns, on your terms", desc: "Unlocks after a few days. Energy trends, recurring friction, where your time actually goes. Signal, not noise." },
  ];

  const SHARED_CSS = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=Bebas+Neue&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    html{scroll-behavior:smooth}
    .fx-noise{position:fixed;inset:0;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");pointer-events:none;z-index:0;opacity:.4}
    .fx-glow{position:fixed;top:-240px;left:50%;transform:translateX(-50%);width:900px;height:700px;background:radial-gradient(ellipse at center,#1fd8f516 0%,transparent 70%);pointer-events:none;z-index:0}
    @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    .a1{opacity:0;animation:fadeUp .6s ease .1s forwards}
    .a2{opacity:0;animation:fadeUp .7s ease .25s forwards}
    .a3{opacity:0;animation:fadeUp .7s ease .4s forwards}
    .a4{opacity:0;animation:fadeUp .7s ease .55s forwards}
    .a5{opacity:0;animation:fadeUp .7s ease .7s forwards}
    .feat-card{background:#0e1e21;border:1px solid #13292d;padding:28px 24px;position:relative;overflow:hidden;transition:border-color .25s,transform .2s}
    .feat-card:hover{border-color:#1fd8f530;transform:translateY(-2px)}
    .feat-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:#1fd8f5;transform:scaleX(0);transform-origin:left;transition:transform .35s ease}
    .feat-card:hover::before{transform:scaleX(1)}
    .fx-input:focus{border-color:#1fd8f5!important;outline:none}
    .fx-btn{width:100%;border:none;border-radius:4px;padding:13px 16px;font-size:11px;letter-spacing:2px;text-transform:uppercase;cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s}
    .fx-btn-red{background:#1fd8f5;color:#fff}
    .fx-btn-red:hover:not(:disabled){background:#6be8ff}
    .fx-btn-red:disabled{opacity:.5;cursor:default}
    .fx-btn-ghost{background:none;border:1px solid #1a383e;color:#316d79}
    .fx-btn-ghost:hover:not(:disabled){border-color:#1fd8f540;color:#859bab}
    .fx-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:18px 48px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #ffffff06;backdrop-filter:blur(14px);background:#060d0f88}
    @media(max-width:680px){
      .fx-nav{padding:14px 20px}
      .fx-page{padding:100px 20px 60px!important}
      .fx-subpage{padding:80px 20px 40px!important;max-width:100%!important}
      .fx-grid-3{grid-template-columns:1fr!important}
      .fx-manifesto-inner{grid-template-columns:1fr!important}
      .fx-footer{padding:24px 20px!important;flex-direction:column;gap:8px;text-align:center}
    }
  `;

  // ---- shared nav ----
  const Nav = ({ showBack, backLabel, onBack }) => (
    <nav className="fx-nav">
      <img src="/kindamorrow-logo.png" alt={APP_NAME} style={{ height: 26, width: "auto", cursor: "pointer", display: "block" }}
        onClick={() => go("landing")} />
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {showBack ? (
          <button onClick={onBack} style={{ fontSize: 10, letterSpacing: 1, color: "#6e93a3", border: "none", background: "none", cursor: "pointer", fontFamily: "inherit" }}>
            &larr; {backLabel || "back"}
          </button>
        ) : (
          <>
            <button onClick={() => go("signin")}
              style={{ fontSize: 10, letterSpacing: 1.5, color: "#6e93a3", textTransform: "uppercase", border: "1px solid #1a383e", padding: "7px 14px", borderRadius: 3, cursor: "pointer", background: "none", fontFamily: "inherit", transition: "all .15s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#1fd8f5"; e.currentTarget.style.color = "#1fd8f5"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#1a383e"; e.currentTarget.style.color = "#316d79"; }}>
              sign in
            </button>
            <button onClick={() => go("signup")}
              style={{ fontSize: 10, letterSpacing: 1.5, color: "#fff", textTransform: "uppercase", border: "1px solid #1fd8f5", padding: "7px 14px", borderRadius: 3, cursor: "pointer", background: "#1fd8f5", fontFamily: "inherit", transition: "all .15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#6be8ff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#1fd8f5"; }}>
              get started
            </button>
          </>
        )}
      </div>
    </nav>
  );

  // ---- shared form error/success ----
  const Msg = () => (<>
    {error && <div style={{ fontSize: 11, color: "#ff6b6b", marginBottom: 12, padding: "10px 12px", background: "#1f0000", borderRadius: 4 }}>{error}</div>}
    {successMsg && <div style={{ fontSize: 11, color: "#10b981", marginBottom: 12, padding: "10px 12px", background: "#001a10", borderRadius: 4 }}>{successMsg}</div>}
  </>);

  // ===========================================================================
  // LANDING PAGE
  // ===========================================================================
  if (page === "landing") return (
    <div style={{ minHeight: "100vh", background: BG_GRADIENT, color: "#f0f0f2", fontFamily: "'Inter',sans-serif", position: "relative", overflowX: "hidden" }}>
      <style>{SHARED_CSS}</style>
      <FeedbackButton />
      <div className="fx-noise" /><div className="fx-glow" />
      <Nav />

      {/* Hero */}
      <div style={{ position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0, background: "linear-gradient(to bottom, #060d0f 0%, rgba(5,5,15,0.72) 18%, rgba(5,5,15,0.78) 70%, #060d0f 100%), url('/kindamorrow.jpg') center 58% / 100% auto no-repeat #11262b" }} />
      <section className="fx-page" style={{ position: "relative", zIndex: 1, maxWidth: 860, margin: "0 auto", padding: "140px 48px 0", textAlign: "center" }}>
        <div className="a1" style={{ fontSize: 10, letterSpacing: 4, color: "#1fd8f5", textTransform: "uppercase", marginBottom: 28, textShadow: "0 1px 8px #000" }}>
          A different kind of daily.
        </div>
        <h1 className="a2" style={{ fontFamily: HEAD_FONT, fontSize: "clamp(72px, 14vw, 170px)", lineHeight: .9, letterSpacing: 2, textTransform: "uppercase", color: "#f0f0f2", marginBottom: 0 }}>
          <em style={{ fontStyle: "italic", color: "#1fd8f5" }}>Kinda.</em>
        </h1>
        <p className="a3" style={{ fontSize: 14, color: "#aebcc6", lineHeight: 1.9, maxWidth: 540, margin: "28px auto 0", textShadow: "0 1px 10px #000" }}>
          A planner and journal built for how your brain actually works. Flexible structure, real insights on your time, and a rhythm that moves with you.
        </p>

        {/* CTA buttons */}
        <div className="a4" style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 40, flexWrap: "wrap", paddingBottom: 64 }}>
          <button onClick={() => go("signup")}
            style={{ background: "#1fd8f5", border: "none", color: "#031a20", borderRadius: 4, padding: "14px 32px", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", transition: "background .15s" }}
            onMouseEnter={e => e.currentTarget.style.background = "#6be8ff"}
            onMouseLeave={e => e.currentTarget.style.background = "#1fd8f5"}>
            Get started &rarr;
          </button>
          <button onClick={() => go("signin")}
            style={{ background: "none", border: "1px solid #1a383e", color: "#7b92a3", borderRadius: 4, padding: "14px 32px", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#1fd8f540"; e.currentTarget.style.color = "#f0f0f2"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#1a383e"; e.currentTarget.style.color = "#7b92a3"; }}>
            Sign in
          </button>
        </div>
      </section>
      </div>

      {/* WHY — visible right after hero scroll */}
      <div style={{ position: "relative", zIndex: 1, borderTop: "1px solid #13292d", borderBottom: "1px solid #13292d", padding: "56px 48px", overflow: "hidden", background: "linear-gradient(135deg, #1fd8f506 0%, transparent 60%)" }}>
        <div className="fx-manifesto-inner" style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: "140px 1fr", gap: 56, alignItems: "center" }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 72, letterSpacing: 2, color: "#1fd8f5", lineHeight: .82, opacity: .12, userSelect: "none", textAlign: "center" }}>
            WHY<br />{APP_NAME_UPPER}
          </div>
          <div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 300, fontSize: "clamp(17px, 2.2vw, 24px)", lineHeight: 1.65, color: "#7b92a3", fontStyle: "italic", marginBottom: 18 }}>
              Most planners are built for people who{" "}
              <strong style={{ color: "#f0f0f2", fontStyle: "normal" }}>don&rsquo;t need planners.</strong>{" "}
              {APP_NAME} is built around the reality that some days the structure is what gets you through &mdash; and the data is what helps you{" "}
              <strong style={{ color: "#f0f0f2", fontStyle: "normal" }}>build better ones.</strong>
            </div>
            <div style={{ fontSize: 10, color: "#6e93a3", letterSpacing: 1 }}>private &middot; no ads &middot; your data is yours</div>
          </div>
        </div>
      </div>

      {/* Features — a notebook page: ruled lines, spiral binding, ticked-off list */}
      <section style={{ position: "relative", zIndex: 1, maxWidth: 820, margin: "0 auto", padding: "80px 20px" }}>
        <div style={{ fontFamily: HEAD_FONT, fontSize: 18, letterSpacing: 4, color: "#6e93a3", textTransform: "uppercase", marginBottom: 28, textAlign: "center" }}>What&rsquo;s inside</div>
        <div style={{ position: "relative", background: "#f1ebdd", borderRadius: 10, padding: "36px 36px 20px 88px", boxShadow: "0 24px 60px rgba(0,0,0,.45)" }}>
          {/* spiral binding */}
          <div aria-hidden="true" style={{ position: "absolute", left: 14, top: 18, bottom: 18, width: 44,
            backgroundImage: "radial-gradient(circle at 30px 18px, #0b1820 0 6px, transparent 7px), linear-gradient(to bottom, transparent 16px, #8f9a9e 16px, #8f9a9e 20px, transparent 20px)",
            backgroundSize: "44px 36px, 40px 36px", backgroundRepeat: "repeat-y", backgroundPosition: "0 0, 0 0" }} />
          {/* margin line */}
          <div aria-hidden="true" style={{ position: "absolute", left: 70, top: 0, bottom: 0, width: 1, background: "#e8a0a0", opacity: .7 }} />
          {/* sticky note */}
          <div aria-hidden="true" style={{ position: "absolute", top: -16, right: 28, transform: "rotate(3deg)", background: "#c7e832", color: "#1b201d", fontFamily: HEAD_FONT, fontSize: 22, letterSpacing: 1, padding: "8px 16px", boxShadow: "0 6px 14px rgba(0,0,0,.3)" }}>no guilt</div>
          {FEATURES.map((f, i) => (
            <div key={f.num} style={{ display: "flex", gap: 18, alignItems: "flex-start", padding: "20px 0", borderBottom: i === FEATURES.length - 1 ? "none" : "1px solid #cfc7b4" }}>
              <div aria-hidden="true" style={{ flexShrink: 0, width: 24, height: 24, marginTop: 3, border: "2px solid #2b2b2e", borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", color: "#0e8f93", fontSize: 20, fontWeight: 700, lineHeight: 1, transform: `rotate(${i % 2 ? 2 : -2}deg)` }}>&#10003;</div>
              <div>
                <div style={{ fontFamily: HEAD_FONT, fontSize: 28, letterSpacing: 1, textTransform: "uppercase", color: "#1b201d", lineHeight: 1.1, marginBottom: 6 }}>{f.title}</div>
                <div style={{ fontSize: 14, color: "#4a4a50", lineHeight: 1.65 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="fx-footer" style={{ position: "relative", zIndex: 1, borderTop: "1px solid #13292d", maxWidth: "100%", padding: "28px 48px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 15, letterSpacing: 3, color: "#6e93a3" }}>{APP_NAME_UPPER}</div>
        <div style={{ fontSize: 10, color: "#6e93a3" }}>
          by <a href="https://madrazallbuilt.com" target="_blank" rel="noopener noreferrer" style={{ color: "#6e93a3", textDecoration: "none" }}>madrazall</a>
          &nbsp;&middot;&nbsp;
          <a href="/privacy.html" style={{ color: "#6e93a3", textDecoration: "none" }}>privacy</a>
          &nbsp;&middot;&nbsp;
          <a href="/terms.html" style={{ color: "#6e93a3", textDecoration: "none" }}>terms</a>
          &nbsp;&middot;&nbsp;
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#6e93a3", textDecoration: "none" }}>contact</a>
        </div>
      </footer>
    </div>
  );

  // ===========================================================================
  // SIGN IN PAGE
  // ===========================================================================
  if (page === "signin") return (
    <div style={{ minHeight: "100vh", background: BG_GRADIENT, color: "#f0f0f2", fontFamily: "'Inter',sans-serif", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <style>{SHARED_CSS}</style>
      <FeedbackButton />
      <div className="fx-noise" /><div className="fx-glow" />
      <Nav showBack backLabel="home" onBack={() => go("landing")} />

      <div className="fx-subpage" style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400, padding: "0 24px" }}>
        <div style={{ background: "#0a171a", border: "1px solid #13292d", borderTop: "2px solid #1fd8f5", borderRadius: 6, padding: "36px 32px" }}>
          <div style={{ fontSize: 9, letterSpacing: 3, color: "#1fd8f5", textTransform: "uppercase", marginBottom: 6 }}>Welcome back</div>
          <div style={{ fontFamily: HEAD_FONT, fontSize: 36, letterSpacing: 1, textTransform: "uppercase", color: "#eef4ff", marginBottom: 28, lineHeight: 1.2 }}>Sign in to {APP_NAME}</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#6e93a3", textTransform: "uppercase", marginBottom: 5 }}>Email</div>
              <input className="fx-input" type="email" placeholder="you@example.com" value={email}
                onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSignIn()}
                disabled={loading} style={iStyle(loading)} />
            </div>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#6e93a3", textTransform: "uppercase", marginBottom: 5 }}>Password</div>
              <input className="fx-input" type="password" placeholder="your password" value={password}
                onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSignIn()}
                disabled={loading} style={iStyle(loading)} />
            </div>
          </div>

          <Msg />

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button className="fx-btn fx-btn-red" onClick={handleSignIn} disabled={loading || !email || !password}>
              {loading ? "signing in..." : "Sign In"}
            </button>
            <button className="fx-btn fx-btn-ghost" onClick={() => go("forgot")}>
              Forgot password?
            </button>
          </div>

          <div style={{ textAlign: "center", marginTop: 20, fontSize: 11, color: "#6e93a3" }}>
            No account?{" "}
            <button onClick={() => go("signup")} style={{ background: "none", border: "none", color: "#1fd8f5", cursor: "pointer", fontFamily: "inherit", fontSize: 11 }}>
              Start free trial
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ===========================================================================
  // FORGOT PASSWORD PAGE
  // ===========================================================================
  if (page === "forgot") return (
    <div style={{ minHeight: "100vh", background: BG_GRADIENT, color: "#f0f0f2", fontFamily: "'Inter',sans-serif", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <style>{SHARED_CSS}</style>
      <FeedbackButton />
      <div className="fx-noise" /><div className="fx-glow" />
      <Nav showBack backLabel="sign in" onBack={() => go("signin")} />

      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400, padding: "0 24px" }}>
        <div style={{ background: "#0a171a", border: "1px solid #13292d", borderTop: "2px solid #1fd8f5", borderRadius: 6, padding: "36px 32px" }}>
          <div style={{ fontSize: 9, letterSpacing: 3, color: "#1fd8f5", textTransform: "uppercase", marginBottom: 6 }}>Account recovery</div>
          <div style={{ fontFamily: HEAD_FONT, fontSize: 36, letterSpacing: 1, textTransform: "uppercase", color: "#eef4ff", marginBottom: 10, lineHeight: 1.2 }}>Reset password</div>
          <div style={{ fontSize: 11, color: "#6e93a3", marginBottom: 24, lineHeight: 1.7 }}>Enter your email and we&rsquo;ll send a reset link. Expires in 1 hour.</div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 9, letterSpacing: 2, color: "#6e93a3", textTransform: "uppercase", marginBottom: 5 }}>Email</div>
            <input className="fx-input" type="email" placeholder="your account email" value={email}
              onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleForgot()}
              disabled={loading} style={iStyle(loading)} />
          </div>

          <Msg />

          <button className="fx-btn fx-btn-red" onClick={handleForgot} disabled={loading || !email}>
            {loading ? "sending..." : "Send Reset Link"}
          </button>
        </div>
      </div>
    </div>
  );

  // ===========================================================================
  // SIGN UP PAGE
  // ===========================================================================
  if (page === "signup") return (
    <div style={{ minHeight: "100vh", background: BG_GRADIENT, color: "#f0f0f2", fontFamily: "'Inter',sans-serif", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <style>{SHARED_CSS}</style>
      <FeedbackButton />
      <div className="fx-noise" /><div className="fx-glow" />
      <Nav showBack backLabel="home" onBack={() => go("landing")} />

      <div className="fx-subpage" style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400, padding: "0 24px" }}>
        <div style={{ background: "#0a171a", border: "1px solid #13292d", borderTop: "2px solid #1fd8f5", borderRadius: 6, padding: "36px 32px" }}>
          <div style={{ fontSize: 9, letterSpacing: 3, color: "#1fd8f5", textTransform: "uppercase", marginBottom: 6 }}>Get started</div>
          <div style={{ fontFamily: HEAD_FONT, fontSize: 36, letterSpacing: 1, textTransform: "uppercase", color: "#eef4ff", marginBottom: 24, lineHeight: 1.2 }}>
            Create your account
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#6e93a3", textTransform: "uppercase", marginBottom: 5 }}>Email</div>
              <input className="fx-input" type="email" placeholder="you@example.com" value={email}
                onChange={e => setEmail(e.target.value)} disabled={loading} style={iStyle(loading)} />
            </div>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#6e93a3", textTransform: "uppercase", marginBottom: 5 }}>Password</div>
              <input className="fx-input" type="password" placeholder="min 6 characters" value={password}
                onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSignUp()}
                disabled={loading} style={iStyle(loading)} />
            </div>
          </div>

          <Msg />

          <button className="fx-btn fx-btn-red" onClick={handleSignUp} disabled={loading || !email || !password}>
            {loading ? "creating account..." : "Create account →"}
          </button>

          <div style={{ textAlign: "center", marginTop: 14, fontSize: 10, color: "#6e93a3", lineHeight: 1.6 }}>
            By creating an account you agree to our <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: "#1fd8f5" }}>terms</a> and <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: "#1fd8f5" }}>privacy policy</a>.
          </div>

          <div style={{ textAlign: "center", marginTop: 20, fontSize: 11, color: "#6e93a3" }}>
            Already have an account?{" "}
            <button onClick={() => go("signin")} style={{ background: "none", border: "none", color: "#1fd8f5", cursor: "pointer", fontFamily: "inherit", fontSize: 11 }}>
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return null;
}

// ══════════════════════════════════════════════════════════════════════
export default function App() {
  const [session, setSession]               = useState(null);
  const [authLoading, setAuthLoading]       = useState(true);
  const [showResetForm, setShowResetForm]   = useState(false);
  const [resetPassword, setResetPassword]   = useState("");
  const [resetLoading, setResetLoading]     = useState(false);
  const [resetError, setResetError]         = useState("");
  const [resetSuccess, setResetSuccess]     = useState(false);
  const [showLateNightPrompt, setShowLateNightPrompt] = useState(false);
  const [lateNightKey, setLateNightKey]     = useState(null);
  const [view, setView]                     = useState(window.location.hash === "#drop" ? "drop" : "today");
  const [blocks, setBlocks]                 = useState([]);
  const [tasks, setTasks]                   = useState([]);
  const [events, setEvents]                 = useState([]);
  const [tags, setTags]                     = useState(SEED_TAGS);
  const [mood, setMood]                     = useState(2);
  const [dayNote, setDayNote]               = useState("");
  const [wins, setWins]                     = useState("");
  const [hard, setHard]                     = useState("");
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalInput, setJournalInput]     = useState("");
  const [journalPromptOffset, setJournalPromptOffset] = useState(0);
  const [journalSaving, setJournalSaving]   = useState(false);
  const [journalFeedback, setJournalFeedback] = useState(null);
  const [journalType, setJournalType]       = useState("note");
  const journalInputRef = useRef(null);
  const [archive, setArchive]               = useState({});
  const [expandedArchive, setExpandedArchive] = useState(null);
  const [flash, setFlash]                   = useState(null);
  const [archiveModal, setArchiveModal]     = useState(null); // null | { keepIds: Set }
  const [showAccount, setShowAccount]       = useState(false);
  const [gcalEvents, setGcalEvents]         = useState([]);
  const [gcalConnected, setGcalConnected]   = useState(false);
  const [dbLoading, setDbLoading]           = useState(false);
  const [archiveTime, setArchiveTime]       = useState(() => localStorage.getItem("flux_archive_time") || "23:00");
  const [autoArchivedToday, setAutoArchivedToday] = useState(() => localStorage.getItem("flux_autoarchived_" + todayKey()) === "true");
  const currentDayKey = todayKey();
  const timelineScrollRef = useRef(null);
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    // TEMP DEV BYPASS (screenshots only) - remove before commit
    if (window.location.hostname === "localhost" && window.location.search.includes("devbypass")) {
      setSession({ user: { id: "00000000-0000-0000-0000-000000000000", email: "demo@example.com" } });
      const mk = (time, duration, label, tag, note) => ({ id: genId(), time, duration, label, tag, note: note || "" });
      const dayBlocks = [
        mk("7:00am", 30, "slow morning + coffee", "personal"),
        mk("8:00am", 60, "deep work: client proposal", "work", "outline first, polish later"),
        mk("9:30am", 30, "inbox + messages", "work"),
        mk("10:30am", 45, "walk the dog", "surviving"),
        mk("12:00pm", 45, "lunch + reset", "human stuff"),
        mk("1:00pm", 90, "design review prep", "work"),
        mk("3:00pm", 30, "call mom", "personal"),
        mk("4:00pm", 60, "grocery run + errands", "surviving"),
        mk("6:00pm", 45, "cook dinner", "human stuff"),
      ];
      const dayTasks = [
        { id: genId(), label: "send invoice to client", done: true, addedAt: nowStamp(), scheduledFor: null },
        { id: genId(), label: "book dentist appointment", done: false, addedAt: nowStamp(), scheduledFor: null },
        { id: genId(), label: "refill prescriptions", done: false, addedAt: nowStamp(), scheduledFor: null },
        { id: genId(), label: "reply to landlord", done: false, addedAt: nowStamp(), scheduledFor: null },
        { id: genId(), label: "plan weekend trip", done: false, addedAt: nowStamp(), scheduledFor: null },
      ];
      const pastArchive = {};
      for (let i = 1; i <= 6; i++) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const k = localDateKey(d);
        pastArchive[k] = {
          blocks: dayBlocks.slice(0, 4 + (i % 5)).map(b => ({ ...b, id: genId() })),
          mood: [3, 2, 4, 1, 3, 2][i - 1], day_note: "steady day", wins: "finished the main thing", hard: "afternoon slump",
          tasks: dayTasks.slice(0, 3), journal_entries: [],
          date: d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }), key: k,
        };
      }
      const nowIso = new Date().toISOString();
      const jmk = (type, text, pinned) => ({ id: genId(), user_id: "demo", day_key: localDateKey(), type, text, pinned: !!pinned, archived: false, source: "manual", mood_at_entry: 3, position: null, created_at: nowIso, deleted_at: null });
      setBlocks(dayBlocks); setTasks(dayTasks); setMood(3);
      setDayNote("good momentum this morning"); setWins("proposal outline done"); setHard("focus dipped after lunch");
      setArchive(pastArchive);
      setJournalEntries([
        jmk("done", "outlined the client proposal before 9", true),
        jmk("note", "walk helped clear my head"),
        jmk("stuck", "can't decide on the pricing tier"),
        jmk("follow_up", "ask design team about the revised mockups"),
      ]);
      setAuthLoading(false);
      return undefined;
    }
    supabase.auth.getSession().then(({ data: { session } }) => { setSession(session); setAuthLoading(false); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => { setSession(session); });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (window.location.hash.includes("type=recovery")) setShowResetForm(true);
  }, []);

  useEffect(() => {
    if (!session) return;
    const hour = new Date().getHours();
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    const yk = localDateKey(yesterday);
    const hasPromptedForYesterday = Boolean(localStorage.getItem("flux_latenight_" + yk));
    if (shouldShowLateNightPrompt({ session, hour, blocks, tasks, wins, hard, dayNote, hasPromptedForYesterday })) {
      setLateNightKey(yk);
      setShowLateNightPrompt(true);
    }
  }, [session, blocks, tasks, wins, hard, dayNote]);

  useEffect(() => {
    setJournalPromptOffset(0);
  }, [currentDayKey]);

  useEffect(() => {
    if (!journalFeedback) return undefined;
    const timer = setTimeout(() => setJournalFeedback(null), 1800);
    return () => clearTimeout(timer);
  }, [journalFeedback]);

  function handleLateNightChoice(choice) {
    setShowLateNightPrompt(false);
    localStorage.setItem("flux_latenight_" + lateNightKey, "true");
    if (choice === "today") {
      const undone = tasks.filter(t => !t.done);
      setTasks(undone.map(t => ({ ...t, addedAt: (t.addedAt || "") + " (rolled)" })));
      setBlocks([]); setMood(2); setDayNote(""); setWins(""); setHard("");
    }
  }

  const loadData = useCallback(async () => {
    if (!session) return;
    setDbLoading(true);
    const uid = session.user.id;
    try {
      const [{ data: userData }, { data: archiveData }, { data: eventsData }, { data: journalData }] = await Promise.all([
        supabase.from("user_data").select("*").eq("user_id", uid).single(),
        supabase.from("archive").select("*").eq("user_id", uid),
        supabase.from("events").select("*").eq("user_id", uid),
        supabase.from("journal_entries").select("*").eq("user_id", uid).eq("day_key", todayKey()).is("deleted_at", null),
      ]);
      if (userData) {
        if (userData.tags) setTags(userData.tags);
        if (userData.today_key === todayKey()) {
          if (userData.blocks) setBlocks(userData.blocks.map(b => ({ ...b, duration: b.duration || DEFAULT_DURATION })));
          if (userData.tasks) setTasks(userData.tasks);
          if (userData.mood !== undefined) setMood(userData.mood);
          if (userData.day_note) setDayNote(userData.day_note);
          if (userData.wins) setWins(userData.wins);
          if (userData.hard) setHard(userData.hard);
        } else {
  // CHANGE 3: always start new day with empty blocks — no demo defaults
  const undone = (userData.tasks || []).filter(t => !t.done && (!t.scheduledFor || t.scheduledFor <= todayKey()));
  const scheduled = (userData.tasks || []).filter(t => !t.done && t.scheduledFor && t.scheduledFor > todayKey());
  setTasks([...undone.map(t => ({ ...t, addedAt: (t.addedAt || "") + " (rolled)" })), ...scheduled]);
  setBlocks([]); // <-- empty, no demos
  setMood(2); setDayNote(""); setWins(""); setHard("");
}
      }
      if (archiveData) {
        const archiveObj = {};
        archiveData.forEach(row => { archiveObj[row.day_key] = row.data; });
        setArchive(archiveObj);
      }
      if (eventsData) setEvents(eventsData.map(e => e.data));
      if (journalData) setJournalEntries(sortJournalEntries(journalData));
    } catch (e) { console.error("load error", e); }
    dataLoadedRef.current = true;
    setDbLoading(false);
  }, [session]);

  const saveToday = useCallback(async (quiet = false) => {
    if (!session) return;
    if (!dataLoadedRef.current) return; // never overwrite before initial load completes
    const uid = session.user.id;
    await supabase.from("user_data").upsert({ user_id: uid, today_key: todayKey(), blocks, tasks, mood, day_note: dayNote, wins, hard, tags }, { onConflict: "user_id" });
    if (!quiet) { setFlash("saved"); setTimeout(() => setFlash(null), 1600); }
  }, [session, blocks, tasks, mood, dayNote, wins, hard, tags]);

  useEffect(() => { if (!session) return; loadData(); }, [session, loadData]);

  // Google Calendar — detect OAuth return & fetch today's events
  useEffect(() => {
    if (!session) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal_connected") === "1") {
      setGcalConnected(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("gcal_error")) {
      console.warn("Google Calendar error:", params.get("gcal_error"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [session]);

  useEffect(() => {
    if (!session) return;
    const dateStr = todayKey();
    fetch(`/api/google-calendar?user_id=${session.user.id}&date=${dateStr}`)
      .then(r => r.json())
      .then(data => {
        if (data.events) {
          setGcalConnected(true);
          setGcalEvents(data.events);
        }
      })
      .catch(() => {});
  }, [session]);

  useEffect(() => {
    if (!session) return;
    const timer = setTimeout(() => { saveToday(false); }, 1000);
    return () => clearTimeout(timer);
  }, [session, saveToday]);

  function openArchiveModal() {
    if (!blocks.length) { archiveDay(new Set()); return; }
    setArchiveModal({ keepIds: new Set(blocks.map(b => b.id)) });
  }

  async function archiveDay(keepIds = new Set()) {
    if (!session) return;
    setArchiveModal(null);
    const uid = session.user.id;
    let updatedTags = [...tags];
    blocks.forEach(b => { if (b.tag) updatedTags = bumpTagUse(b.tag, updatedTags); });
    setTags(updatedTags);
    const activeJournalEntries = sortJournalEntries(journalEntries.filter(e => !e.deleted_at));
    const dayData = { blocks, mood, day_note: dayNote, wins, hard, tasks, journal_entries: activeJournalEntries, date: today(), key: todayKey() };
    await Promise.all([
      supabase.from("archive").upsert({ user_id: uid, day_key: todayKey(), data: dayData }, { onConflict: "user_id,day_key" }),
      supabase.from("user_data").upsert({ user_id: uid, today_key: todayKey(), blocks, tags: updatedTags, mood, day_note: dayNote, wins, hard, tasks }, { onConflict: "user_id" }),
    ]);
    setArchive({ ...archive, [todayKey()]: dayData });
    localStorage.setItem("flux_autoarchived_" + todayKey(), "true");
    setAutoArchivedToday(true);
    const undone = tasks.filter(t => !t.done && (!t.scheduledFor || t.scheduledFor <= todayKey()));
    const scheduled = tasks.filter(t => !t.done && t.scheduledFor && t.scheduledFor > todayKey());
    setTasks([...undone.map(t => ({ ...t, addedAt: (t.addedAt || "") + " (rolled)" })), ...scheduled]);
    const keptBlocks = blocks.filter(b => keepIds.has(b.id));
    setBlocks(keptBlocks);
    setMood(2);
    setDayNote("");
    setWins("");
    setHard("");
    setJournalEntries([]);
    setFlash("archived"); setTimeout(() => setFlash(null), 2000);
  }

  useEffect(() => {
    if (!session) return;
    function checkAutoArchive() {
      const now = new Date();
      const currentH = now.getHours();
      const currentM = now.getMinutes();
      const [targetH, targetM] = archiveTime.split(":").map(Number);
      const hasContent = blocks.length > 0 || tasks.length > 0 || wins || hard || dayNote || journalEntries.length > 0;
      if (currentH === targetH && currentM === targetM && !autoArchivedToday && hasContent) {
        archiveDay(new Set());
      }
      if (currentH === 1 && currentM === 0) {
        localStorage.removeItem("flux_autoarchived_" + todayKey());
        setAutoArchivedToday(false);
      }
    }
    checkAutoArchive();
    const interval = setInterval(checkAutoArchive, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, archiveTime, autoArchivedToday, blocks, tasks, wins, hard, dayNote, journalEntries]);

  async function deleteArchiveDay(dayKey) {
    if (!session) return;
    const uid = session.user.id;
    await supabase.from("archive").delete().eq("user_id", uid).eq("day_key", dayKey);
    const updated = { ...archive }; delete updated[dayKey]; setArchive(updated);
  }

  async function saveEvents(newEvents) {
    if (!session) return;
    const uid = session.user.id;
    const previousEvents = events;
    setEvents(newEvents);
    try {
      const { data: existingRows, error: selectError } = await supabase
        .from("events")
        .select("id,event_id")
        .eq("user_id", uid);
      if (selectError) throw selectError;

      const existingByEventId = new Map((existingRows || []).map(row => [row.event_id, row]));
      for (const eventItem of newEvents) {
        const existing = existingByEventId.get(eventItem.id);
        if (existing) {
          const { error: updateError } = await supabase
            .from("events")
            .update({ data: eventItem })
            .eq("id", existing.id)
            .eq("user_id", uid);
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from("events")
            .insert({ user_id: uid, event_id: eventItem.id, data: eventItem });
          if (insertError) throw insertError;
        }
      }

      const staleRowIds = computeStaleEventRowIds(existingRows || [], newEvents);

      if (staleRowIds.length > 0) {
        const { error: deleteError } = await supabase
          .from("events")
          .delete()
          .eq("user_id", uid)
          .in("id", staleRowIds);
        if (deleteError) throw deleteError;
      }
    } catch (error) {
      setEvents(resolveSavedEvents(previousEvents, newEvents, true));
      console.error("save events error", error);
    }
  }

  async function addJournalEntry() {
    const text = journalInput.trim();
    if (!session || !text || journalSaving) return;
    setJournalSaving(true);
    setJournalFeedback(null);
    const payload = {
      user_id: session.user.id,
      day_key: todayKey(),
      type: journalType,
      text,
      pinned: false,
      archived: false,
      source: "manual",
      mood_at_entry: mood,
    };
    const { data, error } = await supabase.from("journal_entries").insert(payload).select("*").single();
    if (!error && data) {
      setJournalEntries(sortJournalEntries([...journalEntries, data]));
      setJournalInput("");
      setJournalPromptOffset(0);
      setJournalFeedback({ type: "ok", message: "saved" });
      setTimeout(() => journalInputRef.current?.focus(), 50);
    } else {
      setJournalFeedback({ type: "err", message: "couldn't save journal entry" });
      console.error("journal save error", error);
    }
    setJournalSaving(false);
  }

  async function toggleJournalPinned(entryId, nextPinned) {
    if (!session) return;
    const { data, error } = await supabase
      .from("journal_entries")
      .update({ pinned: nextPinned, updated_at: new Date().toISOString() })
      .eq("id", entryId)
      .eq("user_id", session.user.id)
      .select("*")
      .single();
    if (!error && data) {
      setJournalEntries(sortJournalEntries(journalEntries.map(e => e.id === entryId ? data : e)));
    }
  }

  async function setJournalEntryType(entryId, nextType) {
    if (!session) return;
    const { data, error } = await supabase
      .from("journal_entries")
      .update({ type: nextType, updated_at: new Date().toISOString() })
      .eq("id", entryId)
      .eq("user_id", session.user.id)
      .select("*")
      .single();
    if (!error && data) {
      setJournalEntries(sortJournalEntries(journalEntries.map(e => e.id === entryId ? data : e)));
    }
  }

  async function softDeleteJournalEntry(entryId) {
    if (!session) return;
    const { error } = await supabase
      .from("journal_entries")
      .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq("id", entryId)
      .eq("user_id", session.user.id);
    if (!error) {
      setJournalEntries(journalEntries.filter(e => e.id !== entryId));
    }
  }

  async function persistTags(t) {
    setTags(t);
    if (!session) return;
    await supabase.from("user_data").upsert({ user_id: session.user.id, today_key: todayKey(), tags: t }, { onConflict: "user_id" });
  }

  async function handlePasswordReset() {
    setResetError("");
    if (resetPassword.length < 6) { setResetError("Password must be at least 6 characters"); return; }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: resetPassword });
      if (error) setResetError(error.message);
      else { setResetSuccess(true); setTimeout(() => { setShowResetForm(false); setResetPassword(""); window.location.hash = ""; setResetSuccess(false); }, 2000); }
    } catch (e) { setResetError(e.message); }
    setResetLoading(false);
  }

  const archiveCount = Object.keys(archive).length;
  const patterns = computePatterns(archive, tags);
  const promotedTags = tags.filter(t => t.pinned || (t.uses || 0) >= PROMOTE_THRESHOLD);
  const todayJournalEntries = sortJournalEntries(journalEntries.filter(e => !e.deleted_at));
  const isEndOfDay = new Date().getHours() >= 17;
  const journalPromptPool = isEndOfDay ? JOURNAL_PROMPTS_EOD : JOURNAL_PROMPTS_DAY;
  const journalPrompt = pickPrompt(journalPromptPool, todayKey(), todayJournalEntries.length, journalPromptOffset);
  const completedTasksCount = tasks.filter(t => t.done).length;
  const carryoversCount = tasks.filter(t => !t.done).length;
  const followUpOrStuckCount = todayJournalEntries.filter(e => e.type === "follow_up" || e.type === "stuck").length;

  if (showResetForm) {
    return (
      <div style={{ minHeight: "100vh", background: BG_GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "100%", maxWidth: 320 }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <img src="/kindamorrow-logo.png" alt={APP_NAME} style={{ height: 34, width: "auto", display: "block", margin: "0 auto 10px" }} />
            <div style={{ fontSize: 12, color: C.textDim }}>reset your password</div>
          </div>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 24 }}>
            <input type="password" placeholder="new password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handlePasswordReset()} disabled={resetLoading}
              style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "10px 12px", fontSize: 13, outline: "none", marginBottom: 8, opacity: resetLoading ? 0.6 : 1 }} />
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 16 }}>min 6 characters</div>
            {resetError && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 12, padding: 10, background: "#1f0000", borderRadius: 4, textAlign: "center" }}>{resetError}</div>}
            {resetSuccess && <div style={{ fontSize: 12, color: "#10b981", marginBottom: 12, padding: 10, background: "#001f00", borderRadius: 4, textAlign: "center" }}>Password updated! Redirecting...</div>}
            <button onClick={handlePasswordReset} disabled={resetLoading || !resetPassword}
              style={{ width: "100%", background: C.accent, border: "none", color: "#031a20", borderRadius: 4, padding: "10px 16px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", opacity: resetLoading || !resetPassword ? 0.6 : 1 }}>
              {resetLoading ? "updating..." : "Set New Password"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authLoading) return <div style={{ minHeight: "100vh", background: BG_GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontFamily: "monospace" }}>loading...</div>;
  if (!session) return <AuthScreen onSignOut={() => supabase.auth.signOut()} />;

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: BG_GRADIENT, color: C.text, fontFamily: "'Inter','DM Sans','system-ui',sans-serif", fontSize: "13px", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&family=Bebas+Neue&family=DM+Mono:wght@400&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:${C.bg}}::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px}
        textarea,input{font-family:'DM Mono','Fira Code','Courier New',monospace;font-size:13px}
        .task-row:hover .task-del{opacity:1!important}
        .nav{background:none;border:none;border-bottom:2px solid transparent;cursor:pointer;padding:6px 12px;font-family:inherit;font-size:11px;letter-spacing:1.5px;text-transform:uppercase;transition:all .15s}
        .nav.on{color:${C.accent};border-bottom-color:${C.accent}}
        .nav:not(.on){color:${C.textDim}}
        .nav:hover:not(.on){color:${C.textMid}}
        .ac{cursor:pointer;transition:background .15s}
        .ac:hover{background:#152e33!important}
        .tag-chip{display:inline-block;padding:3px 8px;border-radius:3px;font-size:11px;margin:3px 3px 0 0;border:1px solid}
        details summary::-webkit-details-marker{display:none}
        @keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
      `}</style>

      <EarlyAccessBanner />
      {showLateNightPrompt && <LateNightPrompt onChoose={handleLateNightChoice} />}

      {flash && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: flash === "archived" ? "#10b98120" : C.card, border: `1px solid ${flash === "archived" ? "#10b98150" : C.border}`, borderLeft: `3px solid ${flash === "archived" ? "#10b981" : C.accent}`, borderRadius: 6, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, zIndex: 1000, boxShadow: "0 4px 12px rgba(0,0,0,0.3)", animation: "slideUp .35s ease" }}>
          <span style={{ fontSize: 16 }}>{flash === "archived" ? "✓" : "💾"}</span>
          <span style={{ fontSize: 12, color: C.text }}>{flash === "archived" ? "Day archived" : "Changes saved"}</span>
        </div>
      )}

      {archiveModal && (
        <div style={{ position: "fixed", inset: 0, background: "#000000b0", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, width: "100%", maxWidth: 420, padding: "28px 24px", boxShadow: "0 24px 80px #00000080" }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: C.textDim, marginBottom: 8 }}>ARCHIVE DAY</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 2, color: C.text, marginBottom: 6 }}>Which blocks carry forward?</div>
            <div style={{ fontSize: 12, color: C.textMid, marginBottom: 20, lineHeight: 1.6 }}>Checked blocks will stay on tomorrow's timeline. Unchecked ones are saved in the archive only.</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 280, overflowY: "auto", marginBottom: 20 }}>
              {blocks.map(b => {
                const checked = archiveModal.keepIds.has(b.id);
                const startH = Math.floor(b.startMinute / 60) + DAY_START_HOUR;
                const startM = b.startMinute % 60;
                const timeLabel = `${startH % 12 || 12}:${String(startM).padStart(2, "0")} ${startH < 12 ? "am" : "pm"}`;
                return (
                  <label key={b.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: checked ? C.accentDim : C.surface, border: `1px solid ${checked ? C.accent + "50" : C.border}`, borderRadius: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={checked} onChange={() => {
                      const next = new Set(archiveModal.keepIds);
                      checked ? next.delete(b.id) : next.add(b.id);
                      setArchiveModal({ keepIds: next });
                    }} style={{ accentColor: C.accent, width: 14, height: 14 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.label || "Untitled block"}</div>
                      <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>{timeLabel} · {b.duration} min</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setArchiveModal(null)} style={{ background: "none", border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 4, padding: "8px 18px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>cancel</button>
              <button onClick={() => archiveDay(archiveModal.keepIds)} style={{ background: C.accent, border: "none", color: "#031a20", borderRadius: 4, padding: "8px 22px", fontSize: 11, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>archive day →</button>
            </div>
          </div>
        </div>
      )}

      {showAccount && <AccountModal session={session} onClose={() => setShowAccount(false)} />}

      {/* Nav */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: "0 20px", background: C.bg, zIndex: 20, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/kindamorrow-logo.png" alt={APP_NAME} style={{ height: 26, width: "auto", display: "block" }} />
            {dbLoading && <span style={{ fontSize: 11, color: C.textDim }}>syncing...</span>}
          </div>
          <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
            <button className={`nav${view === "today" ? " on" : ""}`} onClick={() => setView("today")}>Today</button>
            <button className={`nav${view === "calendar" ? " on" : ""}`} onClick={() => setView("calendar")}>Calendar</button>
            <button className={`nav${view === "archive" ? " on" : ""}`} onClick={() => setView("archive")}>Archive{archiveCount > 0 ? ` · ${archiveCount}` : ""}</button>
            <button className={`nav${view === "patterns" ? " on" : ""}`} onClick={() => setView("patterns")} style={{ opacity: archiveCount < 3 ? .3 : 1 }}>Patterns</button>
            <HelpSystem />
            <a href="https://ko-fi.com/kindamorrow" target="_blank" rel="noopener noreferrer" style={{ color: C.textDim, fontSize: 10, padding: "6px 8px", textDecoration: "none" }} title="support us ☕">☕</a>
            <button onClick={() => setShowAccount(true)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, fontSize: 10, padding: "6px 8px", fontFamily: "inherit", letterSpacing: 1 }}>account</button>
            <button onClick={() => supabase.auth.signOut()} style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, fontSize: 10, padding: "6px 8px", fontFamily: "inherit", letterSpacing: 1 }}>out</button>
          </div>
        </div>
      </div>

      {/* TODAY — two-column dashboard */}
      {view === "today" && (
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

          {/* LEFT: Timeline */}
          <div style={{ width: "55%", borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Date + mood header */}
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: C.textMid }}>{today()}</div>
              <div style={{ display: "flex", gap: 3 }}>
                {MOODS.map((m, i) => (
                  <button key={i} onClick={() => setMood(i)} style={{ background: mood === i ? C.accentDim : "none", border: mood === i ? `1px solid ${C.accent}` : `1px solid ${C.border}`, borderRadius: 4, padding: "2px 5px", cursor: "pointer", fontSize: 9, color: mood === i ? C.accent : C.textDim, transition: "all .15s", fontFamily: "inherit" }}>{m}</button>
                ))}
              </div>
            </div>
            {/* Tags */}
            {promotedTags.length > 0 && (
              <div style={{ padding: "6px 16px", borderBottom: `1px solid ${C.border}`, flexShrink: 0, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 9, color: C.textDim, letterSpacing: .5 }}>tags:</span>
                {promotedTags.map(t => <TagPill key={t.id} tag={t} small />)}
              </div>
            )}
            {/* Scrollable timeline */}
            <div ref={timelineScrollRef} style={{ flex: 1, overflowY: "auto", padding: "12px 16px 40px" }}>
              {!gcalConnected && (
                <div style={{ marginBottom: 10, display: "flex", justifyContent: "flex-end" }}>
                  <a href={`/api/google-auth?user_id=${session.user.id}`} style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, textDecoration: "none", padding: "4px 10px", border: `1px solid ${C.border}`, borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 12 }}>📅</span> connect google calendar
                  </a>
                </div>
              )}
              <VisualTimeline
                blocks={blocks}
                onBlocksChange={setBlocks}
                tags={tags}
                onPersistTags={persistTags}
                scrollContainerRef={timelineScrollRef}
                gcalEvents={gcalEvents}
              />
            </div>
          </div>

          {/* RIGHT: Tasks + Journal + Upcoming */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px 40px" }}>

              <TaskDrawer tasks={tasks} onTasksChange={setTasks} userId={session.user.id} />
              <UpcomingDrawer events={events} />

              {/* Journal drawer */}
              <JournalDrawer
                journalInput={journalInput}
                setJournalInput={setJournalInput}
                journalInputRef={journalInputRef}
                journalType={journalType}
                setJournalType={setJournalType}
                journalPrompt={journalPrompt}
                setJournalPromptOffset={setJournalPromptOffset}
                addJournalEntry={addJournalEntry}
                journalSaving={journalSaving}
                journalFeedback={journalFeedback}
                todayJournalEntries={todayJournalEntries}
                setJournalEntryType={setJournalEntryType}
                toggleJournalPinned={toggleJournalPinned}
                softDeleteJournalEntry={softDeleteJournalEntry}
              />

              {/* The Download drawer */}
              <DownloadDrawer
                dayNote={dayNote} setDayNote={setDayNote}
                wins={wins} setWins={setWins}
                hard={hard} setHard={setHard}
                completedTasksCount={completedTasksCount}
                carryoversCount={carryoversCount}
                followUpOrStuckCount={followUpOrStuckCount}
                archiveTime={archiveTime} setArchiveTime={setArchiveTime}
                saveToday={saveToday}
                openArchiveModal={openArchiveModal}
              />

            </div>{/* end right scroll */}
          </div>{/* end right col */}
        </div>
      )}{/* end today */}

      {/* OTHER VIEWS — single scrollable column */}
      {view !== "today" && (
        <div style={{ flex: 1, overflowY: "auto", padding: "22px 24px 60px", maxWidth: 760, width: "100%", margin: "0 auto", alignSelf: "flex-start", minWidth: 0 }}>

        {/* CALENDAR */}
        {view === "calendar" && <CalendarView events={events} onEventsChange={saveEvents} />}

        {/* ARCHIVE */}
        {view === "archive" && <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: 3, color: C.textMid }}>ARCHIVE</div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{archiveCount} days logged · click to expand</div>
          </div>
          {archiveCount === 0 && <div style={{ color: C.textDim, fontSize: 13, padding: "40px 0", textAlign: "center" }}>no archived days yet</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {Object.entries(archive).sort((a, b) => b[0].localeCompare(a[0])).map(([key, day]) => {
              const isOpen = expandedArchive === key;
              const doneTasks = (day.tasks || []).filter(t => t.done);
              const pendingTasks = (day.tasks || []).filter(t => !t.done);
              return (
                <div key={key} className="ac" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden" }}
                  onClick={() => setExpandedArchive(isOpen ? null : key)}>
                  <div style={{ padding: "11px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 13 }}>{day.date || key}</div>
                      <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                        {day.blocks?.length || 0} blocks · {MOODS[day.mood ?? 2]}
                        {day.tasks?.length > 0 && <span style={{ marginLeft: 8 }}>{doneTasks.length}/{day.tasks.length} tasks done</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button onClick={e => { e.stopPropagation(); printDay(day, tags); }}
                        style={{ background: "transparent", color: C.textDim, border: `1px solid ${C.border}`, padding: "2px 8px", borderRadius: 4, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>print</button>
                      <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete ${day.date || key}? This cannot be undone.`)) deleteArchiveDay(key); }}
                        style={{ background: "transparent", color: "#ff4d4d", border: "1px solid #ff4d4d", padding: "2px 6px", borderRadius: 4, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>delete</button>
                      <div style={{ color: C.textDim, fontSize: 11 }}>{isOpen ? "▲" : "▼"}</div>
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{ borderTop: `1px solid ${C.border}`, padding: "13px 16px" }} onClick={e => e.stopPropagation()}>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 7 }}>SCHEDULE</div>
                        {(day.blocks || []).sort((a,b) => timeToMinutes(a.time) - timeToMinutes(b.time)).map(b => {
                          const t = resolveTag(b.tag, tags);
                          const dur = b.duration || DEFAULT_DURATION;
                          const durStr = dur >= 60 ? `${Math.floor(dur/60)}h${dur%60 ? dur%60+"m" : ""}` : `${dur}m`;
                          return (
                            <div key={b.id} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 5 }}>
                              <span style={{ fontSize: 10, color: t.color, minWidth: 62 }}>{b.time}</span>
                              <span style={{ fontSize: 10, color: C.textDim, minWidth: 28 }}>{durStr}</span>
                              <span style={{ fontSize: 12, color: C.text }}>{b.label}</span>
                              <span style={{ fontSize: 10, color: t.color, opacity: .7 }}>{t.label}</span>
                              {b.note && <span style={{ fontSize: 11, color: C.textDim, fontStyle: "italic" }}>— {b.note}</span>}
                            </div>
                          );
                        })}
                      </div>
                      {day.tasks?.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 7 }}>TASKS</div>
                          {doneTasks.map(t => <div key={t.id} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: "#10b981" }}>✓</span>
                            <span style={{ fontSize: 12, color: C.textDim, textDecoration: "line-through" }}>{t.label}</span>
                            {t.doneAt && <span style={{ fontSize: 10, color: C.textDim }}>{t.doneAt}</span>}
                            <ReceiptBar receipts={t.receipts} userId={session.user.id} readOnly />
                          </div>)}
                          {pendingTasks.map(t => <div key={t.id} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: C.textDim }}>○</span>
                            <span style={{ fontSize: 12, color: C.textDim }}>{t.label}</span>
                            <span style={{ fontSize: 10, color: C.accent, opacity: .6 }}>rolled over</span>
                          </div>)}
                        </div>
                      )}
                      {day.journal_entries?.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 7 }}>JOURNAL</div>
                          {sortJournalEntries(day.journal_entries).map(j => (
                            <div key={j.id} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4 }}>
                              <span style={{ fontSize: 10, color: C.textDim, minWidth: 60 }}>{JOURNAL_TYPE_LABELS[j.type] || j.type}</span>
                              <span style={{ fontSize: 12, color: C.text }}>{j.text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {day.wins && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 3 }}>WINS</div><div style={{ fontSize: 12, lineHeight: 1.6 }}>{day.wins}</div></div>}
                      {day.hard && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 3 }}>HARD STUFF</div><div style={{ fontSize: 12, lineHeight: 1.6 }}>{day.hard}</div></div>}
                      {day.day_note && <div><div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 3 }}>NOTES</div><div style={{ fontSize: 12, lineHeight: 1.6 }}>{day.day_note}</div></div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>}

        {/* PATTERNS */}
        {view === "patterns" && <>
          {archiveCount < 3 ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
              <div style={{ color: C.textMid, fontSize: 14, marginBottom: 6 }}>not enough data yet</div>
              <div style={{ color: C.textDim, fontSize: 12 }}>archive {3 - archiveCount} more day{3 - archiveCount !== 1 ? "s" : ""} to unlock</div>
            </div>
          ) : <>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: 3, color: C.textMid }}>PATTERNS</div>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{patterns.totalDays} days · no judgment, just signal</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
              {[
                { label: "streak", value: `${patterns.streak}d`, sub: "in a row" },
                { label: "avg energy", value: MOODS[Math.round(patterns.avgMood)]?.split(" ")[0], sub: `${patterns.avgMood} / 4` },
                { label: "task rate", value: patterns.taskCompletionRate != null ? `${patterns.taskCompletionRate}%` : "—", sub: "completed" },
              ].map(s => (
                <div key={s.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "13px 14px", textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontFamily: "'Bebas Neue',sans-serif", color: C.accent, letterSpacing: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: C.textDim, marginTop: 2, letterSpacing: .5 }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: C.textDim }}>{s.sub}</div>
                </div>
              ))}
            </div>
            {patterns.moodData.length >= 3 && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "15px 18px", marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 12 }}>ENERGY — last {patterns.moodData.length} days</div>
                <Sparkline data={patterns.moodData} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                  <span style={{ fontSize: 10, color: C.textDim }}>{patterns.moodData[0]?.date?.split(",")[0]}</span>
                  <span style={{ fontSize: 10, color: C.textDim }}>today</span>
                </div>
              </div>
            )}
            {patterns.dowAvg.length > 0 && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "15px 18px", marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 13 }}>ENERGY BY DAY OF WEEK</div>
                {patterns.dowAvg.slice(0, 5).map(d => <MiniBar key={d.dow} label={d.dow} value={d.avg} max={4} color={d.avg >= 3 ? "#10b981" : d.avg >= 2 ? "#6c63ff" : C.accent} sub={MOODS[Math.round(d.avg)]?.split(" ")[0]} />)}
              </div>
            )}
            {patterns.tagDist.length > 0 && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "15px 18px", marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 13 }}>WHERE YOUR TIME GOES</div>
                {patterns.tagDist.slice(0, 8).map(([tagId, count]) => {
                  const t = resolveTag(tagId, tags);
                  return <MiniBar key={tagId} label={t.label} value={count} max={patterns.tagDist[0][1]} color={t.color} sub={`${count} blocks · ${Math.round(count / patterns.totalBlocks * 100)}%`} />;
                })}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
              {patterns.winWords.length > 0 && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "13px 15px" }}>
                  <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 9 }}>WIN THEMES</div>
                  {patterns.winWords.map(([w, n]) => <span key={w} className="tag-chip" style={{ color: "#10b981", borderColor: "#10b98140", background: "#0d2e22" }}>{w} <span style={{ opacity: .5 }}>&times;{n}</span></span>)}
                </div>
              )}
              {patterns.derailWords.length > 0 && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "13px 15px" }}>
                  <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginBottom: 9 }}>RECURRING FRICTION</div>
                  {patterns.derailWords.map(([w, n]) => <span key={w} className="tag-chip" style={{ color: "#f59e0b", borderColor: "#f59e0b40", background: "#2d2010" }}>{w} <span style={{ opacity: .5 }}>&times;{n}</span></span>)}
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: C.textDim, textAlign: "center", paddingTop: 4, lineHeight: 1.8 }}>
              patterns are observations, not verdicts<br /><span style={{ opacity: .4 }}>signal gets clearer the longer you log</span>
            </div>

          </>}
        </>}

        {view === "drop" && session?.user?.id === DROP_OWNER_ID && (
          <FileDrop userId={session.user.id} />
        )}

        </div>
      )}{/* end other views */}

      <FeedbackButton userEmail={session?.user?.email || ""} />
    </div>
  );
}